import BillingContactsPicker from '../components/BillingContactsPicker';
import { buildBillingContacts } from '../utils/billingContacts';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import Loading from '../components/Loading';
import { listPlants } from '../services/plants';
import { listParts } from '../services/parts';
import {
  listSuppliers,
  getRateFor,
  findAssignment,
  serviceLabel,
} from '../services/suppliers';
import { listTeam } from '../services/team';
import {
  listTimesheets,
  DAY_KEYS,
  OT_RATE_MULTIPLIER,
  dayLabel,
  explanationText,
  weekDates,
} from '../services/timesheets';
import { standardHoursDeviations } from './Timesheets';
import {
  BILLING_MODES,
  createInvoice,
  invoiceHoldsBilling,
  listInvoices,
  nextCreditMemoNumber,
  nextInvoiceNumber,
  nextSupplementalInvoiceNumber,
} from '../services/invoices';
import { calcMileageDollars } from '../config/rates';
import { presetRange } from '../services/billing';
import { formatCountry, formatCurrency, formatDateShort } from '../utils/formatters';
import { displayFullName } from '../utils/teamDisplay';
import { exportInvoicePdf } from '../utils/pdfExport';

/**
 * Invoice draft preview — the page a Director lands on after clicking
 * "Submit Invoice" on the /invoices index. Three jobs:
 *
 *   1. Let the Director pick the billing period and the invoice
 *      mode (combined-multi-plant vs split-per-plant).
 *   2. Show every line that's going to land on the invoice(s),
 *      computed from this supplier's approved-but-unbilled
 *      timesheets, supplements, and credits — with each adjustment
 *      routed according to its BillingDisposition.
 *   3. On Generate, write the invoice record(s) to SharePoint and
 *      render PDFs the Director can download and email out.
 *
 * Adjustment routing matrix (see services/timesheets.js for the
 * disposition constants):
 *   • Supplement / next-invoice          → PPA line on main invoice
 *   • Supplement / supplemental-invoice  → separate -A invoice
 *   • Credit     / next-invoice-credit   → credit line on main invoice
 *   • Credit     / credit-memo           → separate CR-#### memo
 *
 * The main invoice's TimesheetIds + AdjustmentIds capture every row
 * that contributed to it, so the New Invoice list back on /invoices
 * stops offering the same work next time the page loads.
 */
export default function InvoiceDraft({ blank = false }) {
  const { supplierCode: supplierCodeParam } = useParams();
  const navigate = useNavigate();
  const { name, email, isDirector } = useAuth();

  // In blank mode there's no supplier in the URL — the Director picks
  // one from a dropdown, so the effective supplier code comes from
  // local state instead of the route param. Everything downstream
  // (eligibility, billed lookups, generate) keys off `supplierCode`,
  // so the rest of the page doesn't need to know which mode it's in.
  const [selectedSupplierCode, setSelectedSupplierCode] = useState('');
  const supplierCode = blank ? selectedSupplierCode : supplierCodeParam;

  // ---- Loaded data --------------------------------------------------
  const [supplier, setSupplier] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [parts, setParts] = useState([]);
  const [team, setTeam] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---- Form state ---------------------------------------------------
  //
  // Period defaults to the CURRENT month (e.g. Aug 1–31) when Submit
  // Invoice is clicked (per Jillian, Aug 2026). Still editable so a
  // supplier on a 4-4-5 calendar or a weekly rhythm can pick exactly what
  // their cycle covers.
  const [periodStart, setPeriodStart] = useState(() => presetRange('this-month').startDate);
  const [periodEnd, setPeriodEnd] = useState(() => presetRange('this-month').endDate);
  const [issueDate, setIssueDate] = useState(() => todayIso());
  // Due Date follows Issue Date + the Payment Terms days (Net 60 → 60
  // days) until the Director edits it by hand (Oct 2026).
  const [dueDate, setDueDate] = useState(() => addDaysIso(todayIso(), 60));
  const [dueDateTouched, setDueDateTouched] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  // Tracks whether the user has hand-edited the PO field. Once
  // true, the auto-fill effect below stops overwriting their input
  // even when the plant context shifts (e.g., period change pulls
  // a different plant into scope).
  const [poTouched, setPoTouched] = useState(false);
  const [notes, setNotes] = useState('');
  // Whether the generated PDF gets the "Daily Hours Summary" appendix
  // page(s). Off by default (per Jillian, Sep 2026): most customers just
  // want the invoice; the day-by-day breakdown is attached on request.
  const [includeDailyHours, setIncludeDailyHours] = useState(false);
  // Bill Schedule — defaults to the supplier's stored BillingCycle
  // so the customer-facing field matches whatever cadence is on
  // file. Editable in case the Director wants to override for a
  // specific invoice. Stays a free-text input rather than a
  // dropdown so suppliers with custom arrangements (e.g.
  // "First of Month to Last of Month") survive as-is.
  const [billSchedule, setBillSchedule] = useState('');
  const [billScheduleTouched, setBillScheduleTouched] = useState(false);
  // Payment Terms — same auto-fill pattern, sourced from the
  // supplier's stored PaymentTerms (Net 30 / Net 60 / Net 90 / Other
  // free-text). Saved onto the invoice record so finance can trace
  // what terms applied at issue time even if the supplier record
  // later changes.
  // Placeholder is Net 60, the most common terms (per Jillian, Oct 2026);
  // replaced by the supplier's own Payment Terms as soon as it loads.
  const [paymentTerms, setPaymentTerms] = useState('Net 60');
  const [paymentTermsTouched, setPaymentTermsTouched] = useState(false);
  // Manual line items — last-minute lines the Director adds by hand
  // before generating. Stored separately from the auto-computed
  // `lineItems` memo (which recomputes whenever data / filters
  // change) so manual rows survive across period edits and supplier
  // re-loads. Each row carries its own incrementing sourceKey for
  // the Details editor to key off, and a sentinel `isManual: true`
  // flag so the render knows to show editable cells + a Remove
  // button rather than read-only computed values.
  // Blank invoices have no auto-computed lines to seed from, so they
  // open with a single empty manual row ready to fill in. Normal
  // drafts start with none (their lines come from the timesheet pool).
  const [manualLineItems, setManualLineItems] = useState(() =>
    blank ? [makeBlankManualLine(1)] : [],
  );
  const [manualSeq, setManualSeq] = useState(blank ? 2 : 1);
  // `preset` drops the new blank line straight into a section: a
  // plant ({ plantCode, plantTitle }), the On-Call Engagement Fees
  // section ({ section }), or Adjustments ({}).
  const addManualLineItem = (preset = {}) => {
    const idx = manualSeq;
    setManualSeq((n) => n + 1);
    setManualLineItems((prev) => [
      ...prev,
      { ...makeBlankManualLine(idx), ...preset },
    ]);
  };
  const updateManualLine = (sourceKey, patch) =>
    setManualLineItems((prev) =>
      prev.map((li) =>
        li.sourceKey === sourceKey ? applyLinePatch(li, li, patch) : li,
      ),
    );
  const removeManualLine = (sourceKey) =>
    setManualLineItems((prev) =>
      prev.filter((li) => li.sourceKey !== sourceKey),
    );

  // Up to 3 supplier-side contacts the Director picks for this
  // invoice. Stored as an array of 3 slots (each holds the contact
  // name or empty string for "(none)") so the dropdowns stay
  // stable across re-renders. Selected names land on the saved
  // invoice record and surface in the PDF "Bill to" block.
  // Main Billing Contact (printed on the invoice) + Additional Billing
  // Contacts (people to email a copy to; not printed). Oct 2026.
  const [mainContactName, setMainContactName] = useState('');
  const [additionalContactNames, setAdditionalContactNames] = useState([]);
  const [billingMode, setBillingMode] = useState(BILLING_MODES.COMBINED);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  // Result of a successful generation — when populated, the page
  // swaps from the draft form to a success view listing each
  // generated document with its own Download button. Stays around
  // so the Director can re-download if the browser blocked an
  // automatic download.
  const [generated, setGenerated] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [supplierRows, plantRows, partRows, teamRows, tsRows, invRows] =
        await Promise.all([
          listSuppliers(),
          listPlants(),
          listParts(),
          listTeam(),
          listTimesheets(),
          listInvoices(),
        ]);
      setSuppliers(supplierRows || []);
      setPlants(plantRows || []);
      setParts(partRows || []);
      setTeam(teamRows || []);
      setTimesheets(tsRows || []);
      setInvoices(invRows || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Resolve the active supplier from the loaded list whenever the
  // effective supplier code changes. In blank mode this fires each
  // time the Director picks a different supplier from the dropdown —
  // without re-fetching every list, since loadAll runs only once.
  useEffect(() => {
    const target = (supplierCode || '').toUpperCase();
    if (!target) {
      setSupplier(null);
      return;
    }
    const found = (suppliers || []).find(
      (s) => String(s.SupplierCode || '').toUpperCase() === target,
    );
    setSupplier(found || null);
  }, [supplierCode, suppliers]);

  // ---- Already-invoiced lookup -------------------------------------
  //
  // Same approach as the index page: any timesheet / adjustment id
  // that's on a non-Draft invoice for this supplier is excluded
  // from the draft. Drafts don't count — those are in-flight and
  // can be thrown away.
  const billedPairs = useMemo(() => {
    const set = new Set();
    const sc = (supplierCode || '').toUpperCase();
    for (const inv of invoices || []) {
      // Drafts and Cancelled invoices don't hold their trackers.
      if (!invoiceHoldsBilling(inv)) continue;
      if (String(inv.SupplierCode || '').toUpperCase() !== sc) continue;
      for (const tid of inv.timesheetIds || []) set.add(`ts|${tid}`);
      for (const aid of inv.adjustmentIds || []) set.add(`adj|${aid}`);
    }
    return set;
  }, [invoices, supplierCode]);

  // ---- Eligible timesheets / adjustments in the chosen period -----
  //
  // Three buckets per the BillingDisposition routing matrix:
  //   • coreTimesheets — regular Approved rows + Approved supplements
  //     with disposition next-invoice + Approved credits with
  //     disposition next-invoice-credit. All of these become lines on
  //     the main invoice.
  //   • supplementalGroups — Approved supplements with disposition
  //     supplemental-invoice. Each one becomes its own -A invoice.
  //   • creditMemos — Approved credits with disposition credit-memo.
  //     Each one becomes its own CR-#### credit memo document.
  const eligible = useMemo(() => {
    // Blank invoices are never attached to the Billing Tracker — no
    // timesheets, supplements, or credits get pulled in. Every line
    // comes from the manual rows the Director fills in by hand.
    if (blank) return { core: [], supplementals: [], creditMemos: [] };
    const sc = (supplierCode || '').toUpperCase();
    if (!sc) return { core: [], supplementals: [], creditMemos: [] };
    const tsById = new Map(
      (timesheets || []).map((t) => [String(t.id), t]),
    );

    // Eligibility predicate — KEEP any week whose Mon–Sun span
    // overlaps the billing period. The per-day filter inside the
    // line-item builder then strips individual days that fall
    // outside [periodStart, periodEnd] so a week straddling the
    // boundary only contributes its in-range days.
    const inPeriod = (t) => {
      const ws = (t.WeekStart || '').slice(0, 10);
      if (!ws) return false;
      const we = addDaysIso(ws, 6); // Sun of that work week
      if (periodEnd && ws > periodEnd) return false;
      if (periodStart && we < periodStart) return false;
      return true;
    };
    const touchesSupplier = (t) => {
      const rows = Array.isArray(t.rows) ? t.rows : [];
      return rows.some(
        (r) => String(r.supplierCode || '').toUpperCase() === sc,
      );
    };
    // Resolve the supplier an adjustment is billed against. Standalone
    // adjustments (created on the Adjustments tab) carry the supplier
    // explicitly in AdjustmentSupplierCode. Legacy parent-linked
    // adjustments fall back to the FIRST supplier on the parent's rows —
    // the long-standing behavior that keeps a multi-supplier parent from
    // offering the same adjustment on every supplier's invoice.
    const adjustmentSc = (t) => {
      const explicit = String(t.AdjustmentSupplierCode || '')
        .trim()
        .toUpperCase();
      if (explicit) return explicit;
      const parent = tsById.get(String(t.OriginalTimesheetId));
      return String(parent?.rows?.[0]?.supplierCode || '')
        .trim()
        .toUpperCase();
    };

    const core = [];
    const supplementals = [];
    const creditMemos = [];

    for (const t of timesheets || []) {
      if ((t.Status || '') !== 'Approved') continue;
      if (!inPeriod(t)) continue;

      if (t.IsCredit) {
        if (adjustmentSc(t) !== sc) continue;
        if (billedPairs.has(`adj|${t.id}`)) continue;
        if (t.BillingDisposition === 'credit-memo') {
          creditMemos.push(t);
        } else {
          // next-invoice-credit (default) — folded into the main
          // invoice as a credit line.
          core.push(t);
        }
        continue;
      }

      if (t.IsSupplement) {
        // Attribute by the explicit pair (standalone) or, for legacy
        // row-bearing supplements, by the supplier on the supplement's
        // own rows.
        if (adjustmentSc(t) !== sc && !touchesSupplier(t)) continue;
        if (billedPairs.has(`adj|${t.id}`)) continue;
        if (t.BillingDisposition === 'supplemental-invoice') {
          supplementals.push(t);
        } else {
          // next-invoice (default) — folded into the main invoice
          // as a Prior Period Adjustment line.
          core.push(t);
        }
        continue;
      }

      // Regular approved timesheet.
      if (!touchesSupplier(t)) continue;
      if (billedPairs.has(`ts|${t.id}`)) continue;
      core.push(t);
    }
    return { core, supplementals, creditMemos };
  }, [blank, timesheets, supplierCode, periodStart, periodEnd, billedPairs]);

  // Plant code → plant record lookup. Declared above the lineItems
  // memo so we can stamp each line with its plant Title at build
  // time — the PDF reads `plantTitle` off the line items to label
  // the plant banner rows on the printed invoice.
  const plantByCode = useMemo(() => {
    const map = new Map();
    for (const p of plants || []) {
      if (p.PlantCode) map.set(String(p.PlantCode).toUpperCase(), p);
    }
    return map;
  }, [plants]);

  // Plant options offered in each manual line's Plant dropdown. Prefer
  // the selected supplier's assigned plants (the ones they actually
  // cover); fall back to the full plant list when the supplier has no
  // assignments yet. Each option carries the plant Title so a chosen
  // plant can stamp `plantTitle` onto the line for the PDF banner.
  const manualPlantOptions = useMemo(() => {
    const codes = [];
    const seen = new Set();
    const push = (raw) => {
      const code = String(raw || '').trim().toUpperCase();
      if (!code || seen.has(code)) return;
      seen.add(code);
      codes.push(code);
    };
    for (const pa of supplier?.plantAssignments || []) push(pa.plantCode);
    if (codes.length === 0) {
      for (const p of plants || []) push(p.PlantCode);
    }
    return codes
      .map((code) => ({
        value: code,
        title: plantByCode.get(code)?.Title || '',
        label: plantByCode.get(code)?.Title
          ? `${code} — ${plantByCode.get(code).Title}`
          : code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [supplier, plants, plantByCode]);

  // Plant code → distinct part numbers THIS supplier ships to that
  // plant (from the Parts list). Stamped onto each line item as
  // `partNumberLabel` so the PDF can print "Part No. …" in the plant
  // banner — and omit it entirely for plants with no part on file.
  const partNumbersByPlant = useMemo(() => {
    const sc = (supplierCode || '').toUpperCase();
    const map = new Map();
    for (const p of parts || []) {
      if (String(p.SupplierCode || '').toUpperCase() !== sc) continue;
      const num = String(p.PartNumber || '').trim();
      if (!num) continue;
      for (const code of p.plantCodes || []) {
        const key = String(code || '').toUpperCase();
        if (!key) continue;
        if (!map.has(key)) map.set(key, new Set());
        map.get(key).add(num);
      }
    }
    const out = new Map();
    for (const [key, set] of map) out.set(key, Array.from(set).join(', '));
    return out;
  }, [parts, supplierCode]);

  // ---- Build line items --------------------------------------------
  //
  // One line item per timesheet ROW (with a second line per row when
  // the row's labor straddles a mid-week rate change). Per-row
  // granularity is what lets each line carry its own Details cell
  // (initialized from the row's stored explanation, editable in the
  // preview before the invoice is generated).
  //
  // Description is restricted to a fixed vocabulary the customer
  // expects on the invoice:
  //   • "Resident Support Hours"  — coverage type Standard / Other
  //   • "OT Resident Support Hours" — coverage type Overage, billed at the
  //                                 actual hours worked × a 1.5× rate
  //                                 (e.g. 5 worked → 5 hrs @ 1.5× rate)
  //   • "Mileage"                     — coverage type Mileage
  //   • "Expenses"                    — coverage type Expenses
  //   • "Credit"                      — credit (next-invoice-credit
  //                                     disposition); reason goes in
  //                                     the Details column
  //
  // Each line item stamps a stable `sourceKey` derived from the
  // underlying (timesheetId, rowIndex, rate) tuple so the
  // detailsOverrides map can re-target the right line when the
  // memo recomputes after a period change.
  const lineItems = useMemo(() => {
    const sc = (supplierCode || '').toUpperCase();
    if (!sc || !supplier) return [];
    const out = [];

    // Written overage-approval files live on the (plant, supplier)
    // Standard/anchor row of each timesheet. Collect them here keyed by
    // week|plant so the post-pass below can hang the download link on the
    // matching invoice labor line (preferring the OT line).
    const overageApprovalByKey = new Map();
    for (const t of eligible.core) {
      if (t.IsCredit || t.IsSupplement) continue;
      const rows = Array.isArray(t.rows) ? t.rows : [];
      for (const row of rows) {
        if (String(row.supplierCode || '').toUpperCase() !== sc) continue;
        const ct = row.coverageType || 'Standard';
        if (ct !== 'Standard' && ct !== 'Other') continue; // anchor rows
        const fileName = String(row.overageApprovalFileName || '').trim();
        const url = String(row.overageApprovalUrl || '').trim();
        if (!fileName && !url) continue;
        const key = `${t.WeekStart || ''}|${String(row.plantCode || '').toUpperCase()}`;
        overageApprovalByKey.set(key, { fileName, url });
      }
    }

    for (const t of eligible.core) {
      const isCredit = !!t.IsCredit;

      // Credits — rowless adjustments. One line item per credit
      // record with the reason populating Details.
      if (isCredit) {
        const reason =
          t.AdjustmentReason ||
          t.SupplementReason ||
          'over-billing correction';
        const ref = t.OriginalTimesheetId
          ? ` (ref TS ${t.OriginalTimesheetId})`
          : '';
        // Hours-based credit (per Jillian, July 2026): price the hours
        // at the supplier's rate for the adjustment's plant, effective
        // the adjustment's week. Falls back to the flat CreditAmount
        // when no hours were entered. When the plant has no rate on
        // file, the line shows $0 with a "NO RATE ON FILE" flag rather
        // than silently crediting nothing.
        const adjHours = Number(t.AdjustmentHours) || 0;
        const adjPlant = String(t.AdjustmentPlantCode || '').toUpperCase();
        let amount;
        let quantity = 1;
        let unitLabel = '';
        let lineRate = null;
        let creditDescription = 'Credit';
        let orphan = false;
        if (adjHours > 0) {
          quantity = adjHours;
          unitLabel = 'hrs';
          const r = getRateFor(supplier, adjPlant, t.AdjustmentDate || t.WeekStart);
          if (r == null) {
            amount = 0;
            orphan = true;
            creditDescription = 'Credit — NO RATE ON FILE';
          } else {
            lineRate = r;
            amount = -(adjHours * r);
          }
        } else {
          amount = -Math.abs(Number(t.CreditAmount) || 0);
        }
        out.push({
          category: 'Credit',
          coverageType: 'Credit',
          // Hours-based credits carry their plant (needed for the rate
          // lookup above); flat-dollar credits stay unattributed.
          plantCode: adjHours > 0 ? adjPlant || '—' : '—',
          weekStart: t.WeekStart || '',
          // Credit references its parent week. Clamp the displayed
          // range to the billing period so a credit attributed to a
          // boundary week doesn't show dates outside what we're
          // billing now.
          dateRangeStart:
            periodStart && t.WeekStart && t.WeekStart < periodStart
              ? periodStart
              : t.WeekStart || '',
          dateRangeEnd: (() => {
            const ws = t.WeekStart || '';
            if (!ws) return '';
            const we = addDaysIso(ws, 6);
            return periodEnd && we > periodEnd ? periodEnd : we;
          })(),
          description: creditDescription,
          details: `${reason}${ref}`,
          quantity,
          unitLabel,
          rate: lineRate,
          amount,
          orphan: orphan || undefined,
          adjustmentId: t.id,
          sourceKey: `credit|${t.id}`,
          submitterName: t.SubmitterName || '',
          submitterId: String(t.SubmitterId || ''),
        });
        continue;
      }

      // Flat-amount supplements — rowless adjustments (created on the
      // Adjustments tab) that add a single positive dollar line to the
      // invoice. Mirrors the credit branch above but the amount is
      // positive and the plant comes from the adjustment's explicit
      // AdjustmentPlantCode. Legacy supplements that carry rows fall
      // through to the per-row builder below.
      const supplementRowless =
        !!t.IsSupplement && !(Array.isArray(t.rows) && t.rows.length > 0);
      if (supplementRowless) {
        // Hours-based supplement (per Jillian, July 2026): price the
        // hours at the supplier's rate for the adjustment's plant,
        // effective the adjustment's week. Falls back to the flat
        // SupplementAmount when no hours were entered; a no-rate plant
        // shows $0 with a "NO RATE ON FILE" flag.
        const adjHours = Number(t.AdjustmentHours) || 0;
        const flatAmount = Math.abs(Number(t.SupplementAmount) || 0);
        const plantCode =
          String(t.AdjustmentPlantCode || '').toUpperCase() || '—';
        let amount = 0;
        let quantity = 1;
        let unitLabel = '';
        let lineRate = null;
        let description = 'Supplement';
        let orphan = false;
        let render = false;
        if (adjHours > 0) {
          quantity = adjHours;
          unitLabel = 'hrs';
          render = true;
          const r = getRateFor(supplier, plantCode, t.AdjustmentDate || t.WeekStart);
          if (r == null) {
            amount = 0;
            orphan = true;
            description = 'Supplement — NO RATE ON FILE';
          } else {
            lineRate = r;
            amount = adjHours * r;
          }
        } else if (flatAmount > 0) {
          amount = flatAmount;
          render = true;
        }
        if (render) {
          const reason =
            t.AdjustmentReason || t.SupplementReason || 'supplement';
          out.push({
            category: 'Supplement',
            coverageType: 'Supplement',
            plantCode,
            weekStart: t.WeekStart || '',
            dateRangeStart:
              periodStart && t.WeekStart && t.WeekStart < periodStart
                ? periodStart
                : t.WeekStart || '',
            dateRangeEnd: (() => {
              const ws = t.WeekStart || '';
              if (!ws) return '';
              const we = addDaysIso(ws, 6);
              return periodEnd && we > periodEnd ? periodEnd : we;
            })(),
            description,
            details: reason,
            quantity,
            unitLabel,
            rate: lineRate,
            amount,
            orphan: orphan || undefined,
            adjustmentId: t.id,
            sourceKey: `supplement|${t.id}`,
            submitterName: t.SubmitterName || '',
            submitterId: String(t.SubmitterId || ''),
          });
        }
        continue;
      }

      const rows = Array.isArray(t.rows) ? t.rows : [];
      for (let rowIdx = 0; rowIdx < rows.length; rowIdx += 1) {
        const row = rows[rowIdx];
        const rowSc = String(row.supplierCode || '').toUpperCase();
        if (rowSc !== sc) continue;
        const plantCode = String(row.plantCode || '').toUpperCase();
        if (!plantCode) continue;
        const coverageType = row.coverageType || 'Standard';
        // Per-day notes (Sep 2026) flatten to "SU 09/20: ...; M 09/21: ..."
        // for the line's Details; a legacy single note comes through as-is.
        const explanation = explanationText(row, t.WeekStart);
        const rowKey = `${t.id}|${rowIdx}`;

        if (coverageType === 'Expenses') {
          // Expenses are a flat dollar amount attributed to the
          // row's week. Skip if that week's start sits outside the
          // billing period — matches how aggregateBilling decides
          // whether an expense counts for the period.
          const ws = t.WeekStart || '';
          if (periodStart && ws < periodStart) continue;
          if (periodEnd && ws > periodEnd) continue;
          const amount = Number(row.expenseAmount) || 0;
          if (amount > 0) {
            // Expenses span the whole work week conceptually; clamp
            // the displayed range to the billing-period bounds so a
            // row near the boundary doesn't show dates outside what
            // the customer is paying for.
            const weekEnd = ws ? addDaysIso(ws, 6) : '';
            const displayStart =
              periodStart && ws < periodStart ? periodStart : ws;
            const displayEnd =
              periodEnd && weekEnd > periodEnd ? periodEnd : weekEnd;
            out.push({
              category: 'Expenses',
              coverageType: 'Expenses',
              plantCode,
              weekStart: t.WeekStart || '',
              dateRangeStart: displayStart,
              dateRangeEnd: displayEnd,
              description: 'Expenses',
              details: explanation,
              quantity: 1,
              unitLabel: '',
              rate: null,
              amount,
              sourceKey: rowKey,
              submitterName: t.SubmitterName || '',
              submitterId: String(t.SubmitterId || ''),
              plantTitle: plantByCode.get(plantCode)?.Title || '',
              partNumberLabel: partNumbersByPlant.get(plantCode) || '',
              // Carry the uploaded receipt through to the invoice line so
              // the Director can download it right from the draft.
              receiptFileName: row.receiptFileName || '',
              receiptUrl: row.receiptUrl || '',
            });
          }
          continue;
        }

        const values = row.values || {};

        if (coverageType === 'Mileage') {
          // One line per Mileage row, miles summed across the
          // in-period days only. Tracks the first and last
          // contributing day so the rendered Dates column shows
          // exactly the range that produced the miles (not the
          // full Mon-Sun work week).
          let miles = 0;
          let firstDay = '';
          let lastDay = '';
          for (let i = 0; i < DAY_KEYS.length; i += 1) {
            const v = Number(values[DAY_KEYS[i]]) || 0;
            if (v <= 0) continue;
            const dayIso = addDaysIso(t.WeekStart, i);
            if (periodStart && dayIso < periodStart) continue;
            if (periodEnd && dayIso > periodEnd) continue;
            miles += v;
            if (!firstDay || dayIso < firstDay) firstDay = dayIso;
            if (!lastDay || dayIso > lastDay) lastDay = dayIso;
          }
          if (miles > 0) {
            out.push({
              category: 'Mileage',
              coverageType: 'Mileage',
              plantCode,
              weekStart: t.WeekStart || '',
              dateRangeStart: firstDay,
              dateRangeEnd: lastDay,
              description: 'Mileage',
              details: explanation,
              quantity: miles,
              unitLabel: 'mi',
              rate: null,
              amount: calcMileageDollars(miles),
              sourceKey: rowKey,
              submitterName: t.SubmitterName || '',
              submitterId: String(t.SubmitterId || ''),
              plantTitle: plantByCode.get(plantCode)?.Title || '',
              partNumberLabel: partNumbersByPlant.get(plantCode) || '',
            });
          }
          continue;
        }

        // Labor (Standard / Overage / Other) — bucket the row's
        // per-day values by rate so a row straddling a rate change
        // produces one line per rate. Days outside the billing
        // period are skipped before bucketing, so a week
        // straddling the boundary only bills its in-period days.
        // Each rate bucket tracks its first/last contributing day
        // so the Dates column shows the actual range of days that
        // produced those hours (not the full Mon-Sun work week).
        // Orphan hours (no rate on file) get their own zero-amount
        // line so the gap is visible before the Director generates.
        const byRate = new Map();
        let orphanHours = 0;
        let orphanFirstDay = '';
        let orphanLastDay = '';
        // OT Liaison Hours ('Overage') are billed at time-and-a-half by
        // charging the RATE at 1.5× (per Jillian, Aug 2026) — the HOURS
        // stay as actually worked, so the invoice line reads "5 hrs @ 1.5×
        // rate" rather than "7.5 hrs @ standard rate". Dollars are
        // identical to the prior hours-inflated approach; the premium just
        // moves from the hours back onto the rate.
        const isOT = coverageType === 'Overage';
        // Hours are the actual worked hours — no 1.5× applied here.
        const billHours = (v) => v;
        // Itemized day detail for the invoice (per Jillian, Oct 2026):
        // Standard rows list each day that ran over or under the
        // supplier's standard hours; OT rows list every day with hours.
        // Each entry carries the day label, the hours, how far off
        // standard, and that day's note, so the PDF can print them
        // beneath the line while the line itself keeps the total.
        const wkDates = weekDates(t.WeekStart);
        const dev = isOT ? null : standardHoursDeviations(row, plants, suppliers, wkDates);
        const noteFor = (k) => {
          const per = row.dayExplanations;
          const own = per && typeof per === 'object' ? String(per[k] || '').trim() : '';
          return own || '';
        };
        const dayDetailFor = (i) => {
          const k = DAY_KEYS[i];
          const v = Number(values[k]) || 0;
          if (isOT) {
            if (v <= 0) return null;
            return { key: k, label: dayLabel(wkDates[i], k), hours: v, delta: null, note: noteFor(k) };
          }
          const d = dev && dev[k];
          if (!d) return null;
          return {
            key: k,
            label: dayLabel(wkDates[i], k),
            hours: d.actual,
            expected: d.expected,
            delta: Math.round((d.actual - d.expected) * 100) / 100,
            note: noteFor(k),
          };
        };
        for (let i = 0; i < DAY_KEYS.length; i += 1) {
          const v = Number(values[DAY_KEYS[i]]) || 0;
          if (v <= 0) continue;
          const dayIso = addDaysIso(t.WeekStart, i);
          if (periodStart && dayIso < periodStart) continue;
          if (periodEnd && dayIso > periodEnd) continue;
          // The row's `service` picks which of the supplier's rows at
          // this plant prices these hours (Sep 2026: one plant can carry
          // e.g. Standard Coverage and Lineside Support at different
          // rates). Blank on legacy rows → the plant's default row.
          const baseRate = getRateFor(supplier, plantCode, dayIso, row.service);
          if (baseRate == null) {
            orphanHours += billHours(v);
            if (!orphanFirstDay || dayIso < orphanFirstDay) orphanFirstDay = dayIso;
            if (!orphanLastDay || dayIso > orphanLastDay) orphanLastDay = dayIso;
            continue;
          }
          // OT carries its premium on the RATE (1.5× the base rate);
          // Standard bills at the base rate. Hours on the line are the
          // actual hours worked. Rounded to cents for a clean rate value —
          // exact for whole-dollar base rates (per Jillian, Aug 2026).
          const rate = isOT
            ? Math.round(baseRate * OT_RATE_MULTIPLIER * 100) / 100
            : baseRate;
          const existing = byRate.get(rate) || {
            hours: 0,
            firstDay: '',
            lastDay: '',
            dayDetails: [],
            workedDays: [],
          };
          existing.hours += billHours(v);
          const dd = dayDetailFor(i);
          if (dd) existing.dayDetails.push({ ...dd, date: dayIso });
          existing.workedDays.push(dayIso);
          if (!existing.firstDay || dayIso < existing.firstDay) {
            existing.firstDay = dayIso;
          }
          if (!existing.lastDay || dayIso > existing.lastDay) {
            existing.lastDay = dayIso;
          }
          byRate.set(rate, existing);
        }
        // Description matches the on-screen / PDF labels for the two
        // billable hour categories. The stored coverageType stays
        // 'Standard' / 'Overage' (see COVERAGE_TYPE_LABELS in
        // services/timesheets.js); only the display string changes.
        // A non-default service is named on the line so a $35 Standard
        // Coverage line and a $30 Lineside Support line at the same plant
        // are distinguishable on the invoice. The default service is
        // silent so single-service suppliers read exactly as before.
        const rowService = serviceLabel(
          findAssignment(supplier, plantCode, row.service) || { service: row.service },
        );
        const baseDescription =
          (coverageType === 'Overage'
            ? 'OT Resident Support Hours'
            : 'Resident Support Hours') + (rowService ? ` — ${rowService}` : '');
        for (const [rate, { hours, firstDay, lastDay, dayDetails, workedDays }] of byRate) {
          out.push({
            category: 'Liaison Support',
            coverageType,
            plantCode,
            weekStart: t.WeekStart || '',
            dateRangeStart: firstDay,
            dateRangeEnd: lastDay,
            description: baseDescription,
            details: explanation,
            dayDetails,
            // Days in this week that carried hours — the PDF counts them
            // as Production Days when it rolls the weeks into one line.
            workedDays,
            quantity: hours,
            unitLabel: 'hrs',
            rate,
            amount: hours * rate,
            service: rowService,
            sourceKey: `${rowKey}|r${rate}`,
            submitterName: t.SubmitterName || '',
            submitterId: String(t.SubmitterId || ''),
            plantTitle:
              plantByCode.get(plantCode)?.Title || '',
            partNumberLabel: partNumbersByPlant.get(plantCode) || '',
          });
        }
        if (orphanHours > 0) {
          out.push({
            category: 'Liaison Support',
            coverageType,
            plantCode,
            weekStart: t.WeekStart || '',
            dateRangeStart: orphanFirstDay,
            dateRangeEnd: orphanLastDay,
            description: `${baseDescription} — NO RATE ON FILE`,
            details: explanation,
            quantity: orphanHours,
            unitLabel: 'hrs',
            rate: null,
            amount: 0,
            orphan: true,
            sourceKey: `${rowKey}|orphan`,
            submitterName: t.SubmitterName || '',
            submitterId: String(t.SubmitterId || ''),
            plantTitle:
              plantByCode.get(plantCode)?.Title || '',
            partNumberLabel: partNumbersByPlant.get(plantCode) || '',
          });
        }
      }
    }

    // Sort: by plant first (alpha; '—' to the bottom), then by week.
    // Keeps same-plant lines adjacent when the table renders flat.
    out.sort((a, b) => {
      const ap = a.plantCode || '';
      const bp = b.plantCode || '';
      if (ap === '—' && bp !== '—') return 1;
      if (bp === '—' && ap !== '—') return -1;
      if (ap !== bp) return ap.localeCompare(bp);
      return String(a.weekStart || '').localeCompare(String(b.weekStart || ''));
    });

    // Hang each week|plant's overage-approval file on ONE labor line so
    // the invoice shows it once (like the expense receipt link) — the OT
    // line when there is one, otherwise the Standard line.
    for (const [key, appr] of overageApprovalByKey) {
      const sep = key.indexOf('|');
      const wk = key.slice(0, sep);
      const pc = key.slice(sep + 1);
      const candidates = out.filter(
        (li) =>
          li.category === 'Liaison Support' &&
          String(li.plantCode || '').toUpperCase() === pc &&
          String(li.weekStart || '') === wk,
      );
      if (candidates.length === 0) continue;
      const target =
        candidates.find((li) => li.coverageType === 'Overage') || candidates[0];
      target.overageApprovalFileName = appr.fileName;
      target.overageApprovalUrl = appr.url;
    }
    return out;
  }, [
    eligible.core,
    supplier,
    supplierCode,
    plants,
    suppliers,
    plantByCode,
    partNumbersByPlant,
    periodStart,
    periodEnd,
  ]);

  // ---- Editable Details overrides ----------------------------------
  // Keyed by line item `sourceKey` so a period change that
  // recomputes the lineItems memo doesn't blow away the Director's
  // edits — anything that maps to an unchanged source key sticks.
  const [detailsOverrides, setDetailsOverrides] = useState({});
  const detailsFor = useCallback(
    (li) => {
      const override = detailsOverrides[li.sourceKey];
      return override !== undefined ? override : li.details || '';
    },
    [detailsOverrides],
  );
  const setDetailsFor = (sourceKey, value) =>
    setDetailsOverrides((prev) => ({ ...prev, [sourceKey]: value }));

  // ---- On-Call Engagement Fee lines (per Jillian, Oct 2026) --------
  // Every plant where this supplier is On Call AND has labor hours on
  // this invoice gets one "$500 Engagement Fee" line automatically.
  // The fee keeps its real plant code (so per-plant split invoices get
  // their own plant's fee) but shows in its own "On-Call Engagement
  // Fees" section on screen and on the PDF, like Adjustments. It is
  // fully editable (quantity for multiple incidents, rate, amount) and
  // removable like any other computed line.
  const onCallFeeLines = useMemo(() => {
    if (blank || !supplier) return [];
    const onCallCodes = new Set();
    for (const pa of supplier.plantAssignments || []) {
      if ((pa.coverageStatus || '') !== 'On Call') continue;
      const code = String(pa.plantCode || '').trim().toUpperCase();
      if (code) onCallCodes.add(code);
    }
    if (onCallCodes.size === 0) return [];
    const hit = new Set();
    for (const li of lineItems) {
      if (li.category !== 'Liaison Support') continue;
      if ((Number(li.quantity) || 0) <= 0) continue;
      const code = String(li.plantCode || '').trim().toUpperCase();
      if (onCallCodes.has(code)) hit.add(code);
    }
    return Array.from(hit)
      .sort()
      .map((code) => {
        const title = plantByCode.get(code)?.Title || code;
        return {
          sourceKey: `oncall-fee-${code}`,
          section: ON_CALL_FEE_SECTION,
          category: 'On-Call Fee',
          coverageType: 'Engagement Fee',
          plantCode: code,
          plantTitle: title,
          description: 'Engagement Fee',
          details: title,
          submitterName: '',
          weekStart: '',
          dateRangeStart: periodStart || '',
          dateRangeEnd: periodEnd || '',
          quantity: 1,
          unitLabel: '',
          rate: ON_CALL_FEE_AMOUNT,
          amount: ON_CALL_FEE_AMOUNT,
        };
      });
  }, [blank, supplier, lineItems, plantByCode, periodStart, periodEnd]);

  // ---- Fully editable computed lines --------------------------------
  // Every auto-computed line can be hand-edited by the Director
  // (description, liaison, dates, quantity, unit, rate, amount) or
  // removed. Edits are stored per `sourceKey` on top of the computed
  // values, so the original numbers are never lost and "Reset" puts a
  // line back to what the timesheets say.
  const [lineOverrides, setLineOverrides] = useState({});
  const updateComputedLine = (li, patch) =>
    setLineOverrides((prev) => {
      const cur = prev[li.sourceKey] || {};
      return {
        ...prev,
        [li.sourceKey]: applyLinePatch(cur, { ...li, ...cur }, patch),
      };
    });
  const removeComputedLine = (sourceKey) =>
    setLineOverrides((prev) => ({
      ...prev,
      [sourceKey]: { ...(prev[sourceKey] || {}), removed: true },
    }));
  const resetComputedLine = (sourceKey) => {
    setLineOverrides((prev) => {
      const next = { ...prev };
      delete next[sourceKey];
      return next;
    });
    setDetailsOverrides((prev) => {
      const next = { ...prev };
      delete next[sourceKey];
      return next;
    });
  };
  const restoreRemovedLines = () =>
    setLineOverrides((prev) => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        const { removed: _removed, ...rest } = v;
        if (Object.keys(rest).length > 0) next[k] = rest;
      }
      return next;
    });
  const removedLineCount = [...lineItems, ...onCallFeeLines].filter(
    (li) => lineOverrides[li.sourceKey]?.removed,
  ).length;
  const editedComputedLines = useMemo(
    () =>
      [...lineItems, ...onCallFeeLines]
        .filter((li) => !lineOverrides[li.sourceKey]?.removed)
        .map((li) => {
          const o = lineOverrides[li.sourceKey];
          if (!o) return li;
          const { removed: _r, amountTyped: _a, ...fields } = o;
          if (Object.keys(fields).length === 0) return li;
          // `edited` tells the PDF to print this line exactly as the
          // Director typed it instead of rolling it into the
          // consolidated Standard / OT hours rows.
          return { ...li, ...fields, edited: true };
        }),
    [lineItems, onCallFeeLines, lineOverrides],
  );

  // Combined view of every line item that lands on the invoice —
  // the auto-computed lines from the timesheet pool plus any manual
  // last-minute lines the Director added by hand. Used by totals,
  // grouping, plant-code derivation, and the render so the
  // downstream code doesn't have to know about the two sources.
  const allLineItems = useMemo(
    () => [...editedComputedLines, ...manualLineItems],
    [editedComputedLines, manualLineItems],
  );

  // Totals & grouping helpers for the preview render.
  const mainTotal = useMemo(
    () =>
      allLineItems.reduce((acc, li) => acc + (Number(li.amount) || 0), 0),
    [allLineItems],
  );
  const linesByPlant = useMemo(() => {
    const map = new Map();
    for (const li of allLineItems) {
      // Engagement fees get their own section (below the plants,
      // above Adjustments) instead of sitting inside their plant.
      const key =
        li.section === ON_CALL_FEE_SECTION
          ? ON_CALL_FEE_SECTION
          : li.plantCode || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(li);
    }
    const rank = (k) =>
      k === '—' ? 2 : k === ON_CALL_FEE_SECTION ? 1 : 0;
    return Array.from(map.entries()).sort((a, b) => {
      const ra = rank(a[0]);
      const rb = rank(b[0]);
      if (ra !== rb) return ra - rb;
      return String(a[0]).localeCompare(String(b[0]));
    });
  }, [allLineItems]);

  // ---- Hours-Breakdown appendix data --------------------------------
  //
  // Per-plant, per-day liaison hours for the "Weekly Hours Summary"
  // appendix page on the PDF. Unlike the line-item memo (which aggregates
  // hours into per-rate buckets and loses day-level detail), this keeps
  // each day's hours so the appendix can render a Sun→Sat week grid per
  // plant. Standard-row hours are split the same way page 1 splits them
  // (per Jillian, Oct 2026): `standard` holds the supplier's standard
  // hours for the day and `deviation` holds the signed +/- against that
  // standard, so the grid can show an "Overages / Reductions" row. Only
  // in-period LABOR days count — Mileage and Expenses rows never appear
  // in the hours grid. Production Days Billed is the count of distinct
  // in-period days that carried any billed hours for that plant.
  const appendix = useMemo(() => {
    const sc = (supplierCode || '').toUpperCase();
    if (!sc || !supplier) return null;
    // Named plantGroups (not `plants`) so it doesn't hide the plant list
    // that standardHoursDeviations needs below (fixed Oct 2026).
    const plantGroups = new Map();
    for (const t of eligible.core) {
      // Adjustments (credits / rowless supplements) have no day grid.
      if (t.IsCredit || t.IsSupplement) continue;
      const ws = t.WeekStart || '';
      if (!ws) continue;
      const rows = Array.isArray(t.rows) ? t.rows : [];
      for (const row of rows) {
        if (String(row.supplierCode || '').toUpperCase() !== sc) continue;
        const plantCode = String(row.plantCode || '').toUpperCase();
        if (!plantCode) continue;
        const ct = row.coverageType || 'Standard';
        // Labor only — Standard / Overage (OT) / Other carry hours.
        if (ct !== 'Standard' && ct !== 'Overage' && ct !== 'Other') continue;
        const isOT = ct === 'Overage';
        const values = row.values || {};
        const dev = isOT ? null : standardHoursDeviations(row, plants, suppliers, weekDates(ws));
        for (let i = 0; i < DAY_KEYS.length; i += 1) {
          const k = DAY_KEYS[i];
          const v = Number(values[k]) || 0;
          if (v <= 0) continue;
          const dayIso = addDaysIso(ws, i);
          if (periodStart && dayIso < periodStart) continue;
          if (periodEnd && dayIso > periodEnd) continue;
          if (!plantGroups.has(plantCode)) {
            plantGroups.set(plantCode, {
              plantTitle: plantByCode.get(plantCode)?.Title || plantCode,
              liaison: '',
              weeks: new Map(),
              days: new Set(),
            });
          }
          const p = plantGroups.get(plantCode);
          if (!p.liaison && t.SubmitterName) p.liaison = t.SubmitterName;
          if (!p.weeks.has(ws)) {
            p.weeks.set(ws, {
              standard: Array(7).fill(0),
              deviation: Array(7).fill(0),
              ot: Array(7).fill(0),
            });
          }
          const wk = p.weeks.get(ws);
          if (isOT) {
            wk.ot[i] += v;
          } else {
            const d = dev && dev[k];
            const delta = d ? Math.round((d.actual - d.expected) * 100) / 100 : 0;
            wk.standard[i] += v - delta;
            wk.deviation[i] += delta;
          }
          p.days.add(dayIso);
        }
      }
    }
    if (plantGroups.size === 0) return null;
    const out = [];
    for (const [plantCode, p] of plantGroups) {
      const weeks = Array.from(p.weeks.entries())
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
        .map(([weekStart, g]) => ({
          weekStart,
          standard: g.standard,
          deviation: g.deviation,
          ot: g.ot,
        }));
      const standardTotal = weeks.reduce(
        (acc, w) => acc + w.standard.reduce((x, y) => x + y, 0),
        0,
      );
      const deviationTotal = weeks.reduce(
        (acc, w) => acc + w.deviation.reduce((x, y) => x + y, 0),
        0,
      );
      const otTotal = weeks.reduce(
        (acc, w) => acc + w.ot.reduce((x, y) => x + y, 0),
        0,
      );
      out.push({
        plantCode,
        plantTitle: p.plantTitle,
        liaison: p.liaison,
        weeks,
        productionDaysBilled: p.days.size,
        standardTotal,
        deviationTotal,
        otTotal,
        plantTotal: standardTotal + deviationTotal + otTotal,
      });
    }
    out.sort((a, b) => String(a.plantCode).localeCompare(String(b.plantCode)));
    return { plants: out };
  }, [
    eligible.core,
    supplier,
    supplierCode,
    plantByCode,
    plants,
    suppliers,
    periodStart,
    periodEnd,
  ]);

  // Distinct plant codes touched by the main invoice's lines — used
  // for the per-plant split mode and for stamping PlantCodesJson on
  // the saved record.
  const plantCodesOnInvoice = useMemo(() => {
    const set = new Set();
    for (const li of allLineItems) {
      if (li.plantCode && li.plantCode !== '—') set.add(li.plantCode);
    }
    return Array.from(set);
  }, [allLineItems]);

  // ---- On-Call plant detection (banner at the top of the form) -----
  //
  // Surfaces a warning callout when the invoice draft contains LABOR
  // hours for a plant where the supplier's current coverageStatus is
  // "On Call". On-Call coverage carries a 4-hour minimum + a $500
  // engagement fee per incident, so the Director needs the reminder
  // before generating the invoice to make sure those terms get added
  // (typically via the manual line-item button).
  //
  // Gate logic:
  //   • Walk every line item with category 'Liaison Support' and
  //     non-zero quantity — that's the "they billed hours" condition.
  //     Mileage / Expenses / Credits don't trigger the notice on their
  //     own; only the actual hours-of-coverage rows do.
  //   • For each touched plant code, look up the supplier's per-plant
  //     assignment and check coverageStatus === 'On Call'.
  //   • Returns the array of On-Call plant codes that have labor on
  //     this invoice. Empty array means no banner.
  const onCallPlantsWithHours = useMemo(() => {
    if (!supplier) return [];
    const assignmentByCode = new Map();
    for (const pa of supplier.plantAssignments || []) {
      const code = String(pa.plantCode || '').trim().toUpperCase();
      if (code) assignmentByCode.set(code, pa);
    }
    const hit = new Set();
    for (const li of allLineItems) {
      if (li.category !== 'Liaison Support') continue;
      const hrs = Number(li.quantity) || 0;
      if (hrs <= 0) continue;
      const code = String(li.plantCode || '').trim().toUpperCase();
      if (!code || code === '—') continue;
      const a = assignmentByCode.get(code);
      if (a && a.coverageStatus === 'On Call') hit.add(code);
    }
    return Array.from(hit);
  }, [supplier, allLineItems]);

  // ---- Unbilled active plants (per Jillian, Sep 2026) ------------------
  //
  // Some suppliers cover a dozen OEM plants; an invoice that quietly
  // leaves one out is easy to miss. For every plant where this supplier
  // has ACTIVE / ON CALL coverage but no labor line on this draft, work
  // out why and surface it as a warning (plus a confirm on Generate):
  //   • 'none'     no Billing Tracker for that plant/supplier overlaps the
  //                billing period at all
  //   • 'pending'  a tracker exists but is still awaiting approval (only
  //                Approved trackers bill), or is a Draft / Rejected
  //   • 'zero'     an approved tracker exists but carried no hours for
  //                this supplier at that plant
  // Plants whose coverage started after the period ends are skipped.
  const unbilledPlants = useMemo(() => {
    if (blank || !supplier) return [];
    const sc = String(supplier.SupplierCode || '').toUpperCase();
    const billed = new Set(
      allLineItems
        .filter((li) => li.category === 'Liaison Support' && (Number(li.quantity) || 0) > 0)
        .map((li) => String(li.plantCode || '').toUpperCase()),
    );
    const inPeriod = (t) => {
      const ws = (t.WeekStart || '').slice(0, 10);
      if (!ws) return false;
      const we = addDaysIso(ws, 6);
      if (periodEnd && ws > periodEnd) return false;
      if (periodStart && we < periodStart) return false;
      return true;
    };
    const out = [];
    const seen = new Set();
    for (const pa of supplier.plantAssignments || []) {
      const code = String(pa.plantCode || '').trim().toUpperCase();
      if (!code || seen.has(code)) continue;
      const status = pa.coverageStatus || 'Active';
      if (status !== 'Active' && status !== 'On Call') continue;
      const started = String(pa.startDate || '').slice(0, 10);
      if (started && periodEnd && started > periodEnd) continue;
      // Coverage that ended before this period began is not "unbilled",
      // it's over. (Checked per service, so a plant whose Standard
      // Coverage ended but whose Lineside Support is still live still counts.)
      const ended = String(pa.endDate || '').slice(0, 10);
      if (ended && periodStart && ended < periodStart) continue;
      seen.add(code);
      if (billed.has(code)) continue;

      let reason = 'none';
      for (const t of timesheets || []) {
        if (t.IsCredit || t.AdjustmentType) continue;
        if (!inPeriod(t)) continue;
        const rows = Array.isArray(t.rows) ? t.rows : [];
        const touches = rows.some(
          (r) =>
            String(r.supplierCode || '').toUpperCase() === sc &&
            String(r.plantCode || '').toUpperCase() === code,
        );
        if (!touches) continue;
        if ((t.Status || '') === 'Approved') {
          reason = 'zero';
          break;
        }
        reason = 'pending';
      }
      out.push({ code, reason });
    }
    return out;
  }, [blank, supplier, allLineItems, timesheets, periodStart, periodEnd]);

  const UNBILLED_REASON = {
    none: 'no Billing Tracker submitted for this period',
    pending: 'Billing Tracker submitted but not yet approved',
    zero: 'approved tracker carried no hours',
  };

  // Auto-fill Bill Schedule from the supplier's stored BillingCycle.
  // Runs once on supplier load; stops the moment the Director hand-
  // edits the field so we don't overwrite a deliberate change.
  useEffect(() => {
    if (billScheduleTouched) return;
    if (!supplier) return;
    const cycle = String(supplier.BillingCycle || '').trim();
    if (cycle) setBillSchedule(cycle);
  }, [supplier, billScheduleTouched]);

  // Auto-fill Payment Terms from the supplier's stored PaymentTerms.
  // Same one-and-done pattern as Bill Schedule — once the Director
  // types in the field, subsequent supplier loads don't overwrite.
  useEffect(() => {
    if (paymentTermsTouched) return;
    if (!supplier) return;
    const terms = String(supplier.PaymentTerms || '').trim();
    setPaymentTerms(terms || 'Net 60');
  }, [supplier, paymentTermsTouched]);

  // Keep Due Date = Issue Date + the terms' days ("Net 45" → 45) unless
  // the Director has typed their own Due Date. Terms without a number
  // (e.g. "Due on receipt") leave the Due Date as it is.
  useEffect(() => {
    if (dueDateTouched) return;
    const m = String(paymentTerms || '').match(/(\d+)/);
    if (!m || !issueDate) return;
    setDueDate(addDaysIso(issueDate, Number(m[1])));
  }, [paymentTerms, issueDate, dueDateTouched]);

  // Auto-fill the PO Number from the supplier's plantAssignments.
  // POs live on each (supplier, plant) pairing on the supplier record
  // (the per-plant `po` set on the Supplier form), so we look up the
  // assignment(s) matching the plants on this invoice and pre-fill
  // with the distinct PO numbers, comma-joined when the invoice spans
  // plants with different POs. In per-plant split mode this field is
  // just the fallback — each split invoice stamps its own plant's PO
  // (see buildMainInvoiceRecords). Stops as soon as the Director
  // hand-edits the field so we never overwrite a deliberate change.
  useEffect(() => {
    if (poTouched) return;
    if (!supplier) return;
    const codesUpper = plantCodesOnInvoice.map((c) => String(c).toUpperCase());
    if (codesUpper.length === 0) return;
    const distinct = Array.from(
      new Set(codesUpper.map((c) => poForPlant(supplier, c)).filter(Boolean)),
    ).sort();
    if (distinct.length === 0) return;
    setPoNumber(distinct.join(', '));
  }, [supplier, plantCodesOnInvoice, poTouched]);

  // ---- Submitter list for PDF footer / linkage --------------------
  const linkedTimesheetIds = useMemo(
    () => eligible.core.map((t) => String(t.id)),
    [eligible.core],
  );
  const linkedAdjustmentIds = useMemo(() => {
    const ids = [];
    // Adjustments included as lines on the main invoice (next-invoice
    // and next-invoice-credit dispositions). Supplemental and
    // credit-memo adjustments go on their own records and get their
    // own AdjustmentIds list there.
    for (const t of eligible.core) {
      if (t.IsAdjustment) ids.push(String(t.id));
    }
    return ids;
  }, [eligible.core]);

  // ---- Resolve RM / Customer Liaison Manager for the plant -------
  //
  // When the invoice covers a single plant, denormalize that plant's
  // RM and LM onto the record so the Sent Invoices section can show
  // them in the header even if the team roster later changes.
  const denormalizedManagers = useMemo(() => {
    if (plantCodesOnInvoice.length === 0) return { rm: '', lm: '' };
    // For single-plant invoices, attribute. Multi-plant combined
    // invoices land with no single RM/LM — we leave those fields
    // blank. The Sent Invoices section pulls RM/LM from the team
    // roster at render time anyway when the field is blank.
    if (plantCodesOnInvoice.length !== 1) return { rm: '', lm: '' };
    const target = plantCodesOnInvoice[0].toUpperCase();
    let rm = '';
    let lm = '';
    for (const m of team || []) {
      const codes = Array.isArray(m.plantCodes) ? m.plantCodes : [];
      if (!codes.map((c) => String(c).toUpperCase()).includes(target)) continue;
      const label = displayFullName(m);
      if (m.PortalRole === 'RegionalManager' && !rm) rm = label;
      if (m.PortalRole === 'LiaisonManager' && !lm) lm = label;
    }
    return { rm, lm };
  }, [plantCodesOnInvoice, team]);

  // ---- Generate (save + PDF) ---------------------------------------
  const onGenerate = async () => {
    if (!supplier) {
      setGenerationError(
        blank
          ? 'Pick a supplier before generating the invoice.'
          : 'Supplier record not loaded.',
      );
      return;
    }
    // Use allLineItems (auto + manual) so a blank invoice — which has
    // no auto-computed lines, only the rows the Director typed — can
    // still generate as long as it has at least one line.
    if (
      allLineItems.length === 0 &&
      eligible.supplementals.length === 0 &&
      eligible.creditMemos.length === 0
    ) {
      setGenerationError(
        blank
          ? 'Add at least one line item before generating.'
          : 'Nothing to bill in this period. Widen the date range or come back when more Billing Tracker entries are approved.',
      );
      return;
    }
    // Missing-plant guard (per Jillian, Sep 2026): if any active plant for
    // this supplier has no hours on the draft, make the Director confirm
    // before generating so a plant isn't skipped by accident.
    if (unbilledPlants.length > 0) {
      const list = unbilledPlants
        .map(
          (u) =>
            `  • ${plantByCode.get(u.code)?.Title || u.code}: ${UNBILLED_REASON[u.reason]}`,
        )
        .join('\n');
      const ok = window.confirm(
        `${unbilledPlants.length === 1 ? 'One active plant has' : `${unbilledPlants.length} active plants have`} no hours on this invoice:\n\n${list}\n\nGenerate the invoice anyway?`,
      );
      if (!ok) return;
    }
    setGenerating(true);
    setGenerationError('');
    try {
      const generatedDocs = [];

      // Merge the editable Details overrides into the line items
      // before anything saves or renders to PDF. From here on,
      // `lineItemsResolved` is the source of truth for the invoice.
      // Includes manual lines so any last-minute additions the
      // Director typed into the table land on the saved record.
      const lineItemsResolved = allLineItems.map(({ amountTyped: _a, lockedSection: _l, ...li }) => ({
        ...li,
        details: detailsFor(li),
        // Lets the PDF print a hand-edited Details note in addition to
        // the itemized day list (an untouched note would duplicate it).
        detailsEdited: detailsOverrides[li.sourceKey] !== undefined,
        // Coerce manual numeric inputs (strings from the input
        // elements) into real numbers so the PDF and the saved
        // record carry a clean shape.
        quantity:
          li.quantity === '' || li.quantity == null
            ? li.quantity
            : Number(li.quantity),
        rate:
          li.rate === '' || li.rate == null
            ? null
            : Number(li.rate),
        amount: Number(li.amount) || 0,
      }));

      // Compress the three contact slots into a clean list of names
      // (dropping empty / whitespace-only entries). This array is
      // what lands on the saved record and on every PDF the
      // generator produces in this run.
      const chosenContacts = String(mainContactName || '').trim()
        ? [String(mainContactName).trim()]
        : [];
      const billingContacts = buildBillingContacts(
        supplier,
        mainContactName,
        additionalContactNames,
      );

      // ---------- Main invoice (or per-plant split) ----------
      const mainInvoices = await buildMainInvoiceRecords({
        supplier,
        billingMode,
        lineItems: lineItemsResolved,
        plantCodesOnInvoice,
        linesByPlant,
        // Appendix only rides along when the Director ticked the box.
        appendix: includeDailyHours ? appendix : null,
        linkedTimesheetIds,
        linkedAdjustmentIds,
        periodStart,
        periodEnd,
        issueDate,
        dueDate,
        poNumber,
        poTouched,
        notes,
        denormalizedManagers,
        team,
        createdBy: name || email || '',
        contactNames: chosenContacts,
        billingContacts,
        billSchedule,
        paymentTerms,
      });
      for (const doc of mainInvoices) generatedDocs.push(doc);

      // ---------- Supplemental invoices (-A) ----------
      for (const supp of eligible.supplementals) {
        // Each supplemental adjustment gets its own invoice. The
        // parent's invoice number is the parent timesheet's invoice
        // if it's been issued — when this run is also creating that
        // parent's main invoice, link to the first main invoice as
        // the parent so the number sequence (PX-2026-042-A) makes
        // sense in the customer's records.
        const parentInvoiceNumber =
          mainInvoices[0]?.InvoiceNumber ||
          (await nextInvoiceNumber(supplier.SupplierCode, { periodStart, periodEnd }));
        const number = await nextSupplementalInvoiceNumber(parentInvoiceNumber);
        const reason =
          supp.AdjustmentReason || supp.SupplementReason || 'Supplement';
        const lines = buildSupplementLines(supp, supplier);
        const total = lines.reduce(
          (acc, li) => acc + (Number(li.amount) || 0),
          0,
        );
        // A supplement is usually for one plant — stamp that plant's
        // PO when it has one and the Director hasn't hand-edited the
        // PO Number field; otherwise fall back to the draft field.
        const suppPlants = distinctPlants(supp);
        const suppPo =
          !poTouched && suppPlants.length === 1
            ? poForPlant(supplier, suppPlants[0])
            : '';
        const record = await createInvoice({
          InvoiceNumber: number,
          Title: number,
          ParentInvoiceNumber: parentInvoiceNumber,
          Status: 'Sent',
          SupplierCode: supplier.SupplierCode,
          SupplierName: supplier.Title,
          CustomerName: supplier.Title,
          BillingAddress: composeAddress(supplier),
          plantCodes: suppPlants,
          PeriodStart: supp.WeekStart || periodStart,
          PeriodEnd: supp.WeekStart || periodEnd,
          IssueDate: issueDate,
          DueDate: dueDate,
          PoNumber: suppPo || poNumber,
          Notes: `Supplement to ${parentInvoiceNumber}: ${reason}`,
          Total: total,
          RegionalManager: denormalizedManagers.rm,
          CustomerLiaisonManager: denormalizedManagers.lm,
          BillingMode: BILLING_MODES.COMBINED,
          lineItems: lines,
          timesheetIds: [],
          adjustmentIds: [String(supp.id)],
          contactNames: chosenContacts,
          billingContacts,
          BillSchedule: billSchedule || '',
          PaymentTerms: paymentTerms || '',
        });
        generatedDocs.push({
          kind: 'supplemental',
          record,
          pdfPayload: {
            invoice: record,
            periodLabel: formatPeriodLabel(record.PeriodStart, record.PeriodEnd),
            customer: supplier,
            lineItems: lines,
            total,
            isCreditMemo: false,
          },
        });
      }

      // ---------- Credit memos (CR-####) ----------
      for (const cred of eligible.creditMemos) {
        const number = await nextCreditMemoNumber();
        const reason =
          cred.AdjustmentReason || cred.SupplementReason || 'Credit';
        const amount = Number(cred.CreditAmount) || 0;
        const lines = [
          {
            category: 'Credit',
            plantCode: '—',
            weekStart: cred.WeekStart || '',
            description: `Credit — ${reason}`,
            quantity: 1,
            unitLabel: '',
            rate: null,
            amount,
          },
        ];
        const record = await createInvoice({
          InvoiceNumber: number,
          Title: number,
          Status: 'Sent',
          SupplierCode: supplier.SupplierCode,
          SupplierName: supplier.Title,
          CustomerName: supplier.Title,
          BillingAddress: composeAddress(supplier),
          plantCodes: [],
          PeriodStart: cred.WeekStart || periodStart,
          PeriodEnd: cred.WeekStart || periodEnd,
          IssueDate: issueDate,
          DueDate: dueDate,
          PoNumber: poNumber,
          Notes: `Credit memo: ${reason}`,
          Total: amount,
          RegionalManager: denormalizedManagers.rm,
          CustomerLiaisonManager: denormalizedManagers.lm,
          BillingMode: BILLING_MODES.COMBINED,
          contactNames: chosenContacts,
          billingContacts,
          lineItems: lines,
          timesheetIds: [],
          adjustmentIds: [String(cred.id)],
          BillSchedule: billSchedule || '',
          PaymentTerms: paymentTerms || '',
        });
        generatedDocs.push({
          kind: 'credit-memo',
          record,
          pdfPayload: {
            invoice: record,
            periodLabel: formatPeriodLabel(record.PeriodStart, record.PeriodEnd),
            customer: supplier,
            lineItems: lines,
            total: amount,
            isCreditMemo: true,
          },
        });
      }

      setGenerated(generatedDocs);
      // Fire one automatic download per generated document. Most
      // browsers allow several sequential downloads from a button
      // click; the success view also exposes them as buttons so a
      // blocked-by-popup case can still get the PDFs out.
      for (const doc of generatedDocs) {
        exportInvoicePdf(doc.pdfPayload);
      }
    } catch (err) {
      console.error('[invoice draft] generate failed', err);
      setGenerationError(err.message || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  // ---- Render guards ------------------------------------------------
  if (!isDirector) {
    return (
      <div className="card">
        <div className="card-title">Invoice Draft</div>
        <p className="muted">Only Directors can compose invoices.</p>
      </div>
    );
  }
  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load</div>
        <p>{error.message || 'Unknown error.'}</p>
        <button className="btn btn-outline btn-sm" onClick={loadAll}>
          Retry
        </button>
      </div>
    );
  }
  // In blank mode the supplier starts unselected — that's expected, so
  // we fall through to the form (which shows the supplier picker). The
  // "not found" guard only applies to the normal supplier-coded route.
  if (!blank && !supplier) {
    return (
      <div className="card">
        <div className="card-title">Supplier Not Found</div>
        <p className="muted">
          No supplier matches code {supplierCode}.{' '}
          <Link to="/invoices">Back to invoices</Link>.
        </p>
      </div>
    );
  }

  // ---- Success view -------------------------------------------------
  if (generated && generated.length > 0) {
    return (
      <>
        <div className="page-header">
          <h1>Invoice Generated</h1>
          <div className="actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate('/invoices')}
            >
              Back to Invoices
            </button>
          </div>
        </div>
        <div className="card">
          <div className="card-title">
            {generated.length} document{generated.length === 1 ? '' : 's'}{' '}
            generated and saved
          </div>
          <p className="muted">
            PDFs downloaded automatically. Re-download from the list below
            if a browser popup blocker stopped one. Each record is now
            visible under Sent Invoices on the main page.
          </p>
          <table className="mini-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {generated.map((doc) => (
                <tr key={doc.record.id}>
                  <td>{doc.record.InvoiceNumber}</td>
                  <td>
                    {doc.kind === 'credit-memo'
                      ? 'Credit Memo'
                      : doc.kind === 'supplemental'
                        ? 'Supplemental Invoice'
                        : doc.kind === 'main-per-plant'
                          ? 'Per-Plant Invoice'
                          : 'Invoice'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {formatCurrency(Number(doc.record.Total) || 0)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => exportInvoicePdf(doc.pdfPayload)}
                    >
                      Download PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  // ---- Draft preview ------------------------------------------------
  return (
    <>
      <div className="page-header">
        <h1>
          {blank
            ? supplier
              ? `New Blank Invoice — ${supplier.Title || supplier.SupplierCode}`
              : 'New Blank Invoice'
            : `Submit Invoice — ${supplier.Title || supplier.SupplierCode}`}
        </h1>
        <div className="actions">
          <button
            className="btn btn-outline"
            onClick={() => navigate('/invoices')}
            disabled={generating}
          >
            ← Back to Invoices
          </button>
        </div>
      </div>

      {/* On-Call notice — shown when the draft contains labor hours
          at one or more plants where this supplier's coverageStatus
          is "On Call". The 4-hour minimum + $500 engagement fee per
          incident aren't auto-applied; this banner is the Director's
          reminder to add them manually (the "+ Add Line Item" button
          below makes that easy). Lists the affected plant names so
          it's obvious which assignment triggered it. */}
      {unbilledPlants.length > 0 && (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: '12px 16px',
            borderRadius: 8,
            background: '#fffbeb',
            border: '1px solid #f59e0b',
            color: '#92400e',
            fontSize: 14,
            lineHeight: 1.45,
          }}
        >
          <strong>Heads up:</strong> this supplier has active coverage at{' '}
          {unbilledPlants.length === 1 ? 'a plant' : `${unbilledPlants.length} plants`} with
          no hours on this invoice for {formatPeriodLabel(periodStart, periodEnd)}:
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {unbilledPlants.map((u) => (
              <li key={u.code}>
                <strong>{plantByCode.get(u.code)?.Title || u.code}</strong>
                <span className="muted" style={{ marginLeft: 6 }}>{u.code}</span>
                {' '}· {UNBILLED_REASON[u.reason]}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 6, fontSize: 13 }}>
            You can still generate. You'll be asked to confirm first.
          </div>
        </div>
      )}

      {onCallPlantsWithHours.length > 0 && (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: '12px 16px',
            borderRadius: 8,
            background: 'var(--brand-primary-soft, #fff4e6)',
            border: '1px solid var(--brand-primary, #E87722)',
            color: 'var(--brand-primary-dark, #b14a14)',
            fontSize: 14,
            lineHeight: 1.45,
          }}
        >
          <strong>Notice:</strong> This supplier is on call at{' '}
          <strong>
            {onCallPlantsWithHours
              .map((code) => plantByCode.get(code)?.Title || code)
              .join(', ')}
          </strong>
          . A $500 Engagement Fee has been added below in the On-Call
          Engagement Fees section. Change the quantity if there was more
          than one incident. The 4-hour minimum still applies.
        </div>
      )}

      {/* ---------- Supplier Information ----------
          Read-only summary of who's being billed (Account Number,
          composed address) plus up to three contact dropdowns sourced
          from the supplier's contacts list. Selected names save onto
          the invoice record and print on the PDF as an "Attn:" line
          inside the Bill-to block. */}
      <div className="card">
        <div className="card-title">Supplier Information</div>
        {/* Blank-invoice supplier picker — the Director chooses who to
            bill from the existing Suppliers list; the read-only details
            and contact pickers below auto-fill from that record. Hidden
            on the normal route, where the supplier comes from the URL. */}
        {blank && (
          <div className="field" style={{ maxWidth: 460, marginBottom: 16 }}>
            <label>Supplier</label>
            <select
              value={selectedSupplierCode}
              onChange={(e) => setSelectedSupplierCode(e.target.value)}
            >
              <option value="">— Select a supplier —</option>
              {(suppliers || [])
                .filter((s) => s.SupplierCode)
                .slice()
                .sort((a, b) => {
                  const an = (a.Title || a.SupplierCode || '').toLowerCase();
                  const bn = (b.Title || b.SupplierCode || '').toLowerCase();
                  return an.localeCompare(bn);
                })
                .map((s) => (
                  <option key={s.SupplierCode} value={s.SupplierCode}>
                    {s.Title ? `${s.SupplierCode} — ${s.Title}` : s.SupplierCode}
                  </option>
                ))}
            </select>
          </div>
        )}
        {!supplier ? (
          <p className="muted" style={{ margin: 0 }}>
            Pick a supplier above to fill in billing details, then add
            line items below.
          </p>
        ) : (
        /* Override the default auto-fit form-grid with an explicit
            1fr 1fr 2fr layout so Address takes the right two-thirds
            of the row — gives the single-line street/city/country
            string enough room to render in full, and its right edge
            lines up with the Additional Contact cell directly below.
            `.field.full` (grid-column: 1 / -1) continues to span all
            columns for the contacts sub-row. */
        <div
          className="form-grid"
          style={{ gridTemplateColumns: '1fr 1fr 2fr' }}
        >
          <div className="field">
            <label>Account Number</label>
            <div
              style={{
                padding: '8px 10px',
                background: '#fafafa',
                border: '1px solid var(--brand-border, #e5e7eb)',
                borderRadius: 6,
                fontSize: 14,
              }}
            >
              {supplier.SupplierCode || '—'}
            </div>
          </div>
          <div className="field">
            <label>Supplier</label>
            <div
              style={{
                padding: '8px 10px',
                background: '#fafafa',
                border: '1px solid var(--brand-border, #e5e7eb)',
                borderRadius: 6,
                fontSize: 14,
              }}
            >
              {supplier.Title || '—'}
            </div>
          </div>
          <div className="field">
            <label>Address</label>
            <div
              style={{
                padding: '8px 10px',
                background: '#fafafa',
                border: '1px solid var(--brand-border, #e5e7eb)',
                borderRadius: 6,
                fontSize: 14,
                // Render the address on a single line on the form for
                // compactness — composeAddress() still returns the
                // multi-line shape for the PDF / saved record. We just
                // swap the line separators to commas here.
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={composeAddress(supplier).replace(/\n/g, ', ')}
            >
              {composeAddress(supplier)
                ? composeAddress(supplier).replace(/\n/g, ', ')
                : (
                  <span className="muted">
                    No address on file. Add one on the Supplier page.
                  </span>
                )}
            </div>
          </div>
          {/* Main Billing Contact (dropdown, printed on the invoice) and
              Additional Billing Contacts (checklist, emailed a copy, not
              printed). Options come from the supplier's contacts. */}
          <div
            className="field full"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 16,
              padding: 0,
            }}
          >
            <BillingContactsPicker
              contacts={supplier.contacts}
              mainName={mainContactName}
              additionalNames={additionalContactNames}
              onChange={({ mainName, additionalNames }) => {
                setMainContactName(mainName);
                setAdditionalContactNames(additionalNames);
              }}
            />
          </div>
        </div>
        )}
      </div>

      {/* ---------- Billing Schedule & Details ----------
          Hidden on the New Blank Invoice — a from-scratch invoice isn't
          billed against a Billing Tracker period, so the From/To dates,
          Bill Schedule, and Billing Mode controls don't apply there. */}
      {!blank && (
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">Billing Schedule & Details</div>
        <div
          style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            // Use the LABEL row as the alignment anchor so every
            // control sits at the same vertical line regardless of
            // any intrinsic-height differences between <input
            // type="date"> and <select>. flex-end was aligning the
            // bottom edges, which made the dropdown's slightly
            // taller intrinsic height visually drop the field
            // below From / To.
            alignItems: 'flex-start',
          }}
        >
          <PeriodField label="From">
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              style={dateInputStyle}
            />
          </PeriodField>
          <PeriodField label="To">
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              style={dateInputStyle}
            />
          </PeriodField>
          <PeriodField label="Bill Schedule">
            <input
              type="text"
              value={billSchedule}
              onChange={(e) => {
                setBillSchedule(e.target.value);
                setBillScheduleTouched(true);
              }}
              placeholder="e.g. Monthly"
              style={{ ...dateInputStyle, minWidth: 200 }}
            />
          </PeriodField>
          <PeriodField label="Billing Mode">
            <select
              value={billingMode}
              onChange={(e) => setBillingMode(e.target.value)}
              style={{ ...dateInputStyle, minWidth: 380 }}
            >
              <option value={BILLING_MODES.COMBINED}>
                Combined Plants (One Invoice, Multiple Plants)
              </option>
              <option value={BILLING_MODES.PER_PLANT}>
                Per Plant (One Invoice per Plant)
              </option>
            </select>
          </PeriodField>
        </div>
      </div>
      )}

      {/* ---------- Invoice Meta ---------- */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">Invoice Details</div>
        <div className="form-grid">
          <div className="field">
            <label>Issue Date</label>
            <input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                setDueDateTouched(true);
              }}
            />
          </div>
          <div className="field">
            <label>PO Number</label>
            <input
              type="text"
              value={poNumber}
              onChange={(e) => {
                setPoNumber(e.target.value);
                setPoTouched(true);
              }}
              placeholder={
                supplier?.PoRequired === 'Yes'
                  ? 'Required by this supplier'
                  : 'Optional'
              }
            />
            {/* In per-plant split mode each generated invoice stamps its
                own plant's PO, so this field is only the fallback /
                override. Surface that so a comma-joined preview doesn't
                read like every invoice gets both numbers. */}
            {billingMode === BILLING_MODES.PER_PLANT &&
              plantCodesOnInvoice.length > 1 &&
              !poTouched && (
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--brand-muted)',
                    marginTop: 4,
                  }}
                >
                  Split invoices each use their own plant&apos;s PO
                  automatically. Typing here applies one PO number to all.
                </span>
              )}
          </div>
          <div className="field">
            <label>Payment Terms</label>
            <input
              type="text"
              value={paymentTerms}
              onChange={(e) => {
                setPaymentTerms(e.target.value);
                setPaymentTermsTouched(true);
              }}
              placeholder="e.g. Net 30"
            />
          </div>
          <div className="field full">
            <label>Notes</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Payment terms, contact, special instructions…"
            />
          </div>
          {/* Weekly Hours Summary — opt-in appendix (per Jillian, Sep
              2026). Unchecked, the PDF is just the invoice pages. */}
          <div className="field full" style={{ marginTop: 4 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              <input
                type="checkbox"
                checked={includeDailyHours}
                onChange={(e) => setIncludeDailyHours(e.target.checked)}
              />
              Include the Weekly Hours Summary
            </label>
            <span
              className="muted"
              style={{ fontSize: 12, marginTop: 4, display: 'block' }}
            >
              Adds a page to the PDF showing each plant's liaison hours week by
              week, with standard hours, overages or reductions, and OT broken
              out by day. Leave unchecked unless the customer asks for it.
            </span>
          </div>
        </div>
      </div>

      {/* ---------- Line items preview ---------- */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">Line Items</div>
        {allLineItems.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            No labor / mileage / expense lines fall in this period. Adjust
            the dates above, check whether new Billing Tracker entries need
            to be approved first, or add a manual line below.
          </p>
        ) : (
          <table className="mini-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ minWidth: 220 }}>Details</th>
                <th>Liaison</th>
                <th>Dates</th>
                <th style={{ textAlign: 'right' }}>Quantity</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {/* Plant grouping — line items are bucketed by plant
                  via the linesByPlant memo, then each plant gets a
                  banner row above its lines. Removing the per-row
                  Plant column saves horizontal space; the banner
                  carries the plant context for every line inside
                  it. Details is editable; the override map is
                  merged into the saved record on Generate. */}
              {linesByPlant.map(([plant, plantLines]) => (
                <Fragment key={plant}>
                  <tr style={{ background: 'var(--brand-primary-soft, #fff4e6)' }}>
                    <td colSpan={7} style={{ padding: '6px 10px' }}>
                      {plant === ON_CALL_FEE_SECTION ? (
                        <strong style={{ color: 'var(--brand-muted)' }}>
                          On-Call Engagement Fees
                        </strong>
                      ) : plant === '—' ? (
                        <strong style={{ color: 'var(--brand-muted)' }}>
                          {blank ? 'Line Items (No Plant)' : 'Adjustments'}
                        </strong>
                      ) : (
                        <strong
                          style={{
                            color: 'var(--brand-primary-dark, #b14a14)',
                          }}
                        >
                          {plantByCode.get(plant)?.Title || plant}
                        </strong>
                      )}
                    </td>
                  </tr>
                  {plantLines.map((li) =>
                    li.isManual ? (
                      <ManualLineRow
                        key={li.sourceKey}
                        li={li}
                        plantOptions={manualPlantOptions}
                        onChange={(patch) =>
                          updateManualLine(li.sourceKey, patch)
                        }
                        onRemove={() => removeManualLine(li.sourceKey)}
                      />
                    ) : (
                      <ManualLineRow
                        key={li.sourceKey}
                        li={{ ...li, details: detailsFor(li) }}
                        computed
                        edited={
                          !!lineOverrides[li.sourceKey] ||
                          detailsOverrides[li.sourceKey] !== undefined
                        }
                        onChange={(patch) => {
                          const { details, ...rest } = patch;
                          if (details !== undefined)
                            setDetailsFor(li.sourceKey, details);
                          if (Object.keys(rest).length > 0)
                            updateComputedLine(li, rest);
                        }}
                        onRemove={() => removeComputedLine(li.sourceKey)}
                        onReset={() => resetComputedLine(li.sourceKey)}
                        extras={
                          <>
                          {li.coverageType === 'Expenses' &&
                            (li.receiptUrl ? (
                              <div style={{ marginTop: 3 }}>
                                <a
                                  href={li.receiptUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Download the receipt for this expense"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 12,
                                    color: 'var(--brand-primary, #E87722)',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                  }}
                                >
                                  {'\u{1F4CE}'}{' '}
                                  {li.receiptFileName || 'Receipt'}
                                </a>
                              </div>
                            ) : (
                              <div
                                style={{
                                  marginTop: 3,
                                  fontSize: 12,
                                  color: 'var(--brand-muted, #9ca3af)',
                                  fontStyle: 'italic',
                                }}
                              >
                                No receipt attached
                              </div>
                            ))}
                          {(li.overageApprovalUrl ||
                            li.overageApprovalFileName) && (
                            <div style={{ marginTop: 3 }}>
                              <a
                                href={li.overageApprovalUrl || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Download the written overage approval for this supplier"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  fontSize: 12,
                                  color: 'var(--brand-primary, #E87722)',
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                }}
                              >
                                {'\u{1F4CE}'} Overage approval:{' '}
                                {li.overageApprovalFileName || 'file'}
                              </a>
                            </div>
                          )}
                          </>
                        }
                      />
                    ),
                  )}
                  {/* Add a blank, fully editable line to THIS section. */}
                  <tr>
                    <td colSpan={7} style={{ padding: '4px 10px' }}>
                      <button
                        type="button"
                        onClick={() =>
                          addManualLineItem(
                            plant === ON_CALL_FEE_SECTION
                              ? { section: ON_CALL_FEE_SECTION, lockedSection: true }
                              : plant === '—'
                                ? { lockedSection: true }
                                : {
                                    lockedSection: true,
                                    plantCode: plant,
                                    plantTitle:
                                      plantByCode.get(plant)?.Title || plant,
                                  },
                          )
                        }
                        disabled={generating}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          color: 'var(--brand-primary, #E87722)',
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        + Add Line
                      </button>
                    </td>
                  </tr>
                  {/* Per-plant subtotal — sum of every amount in
                      this plant's group. Right-aligned, lighter
                      weight than the grand total so the eye can
                      tell them apart at a glance. */}
                  <tr style={{ background: '#fafafa' }}>
                    <td
                      colSpan={6}
                      style={{
                        textAlign: 'right',
                        color: 'var(--brand-muted)',
                      }}
                    >
                      Subtotal —{' '}
                      {plant === ON_CALL_FEE_SECTION
                        ? 'On-Call Engagement Fees'
                        : plant === '—'
                          ? 'Adjustments'
                          : plantByCode.get(plant)?.Title || plant}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontWeight: 600,
                      }}
                    >
                      {formatCurrency(
                        plantLines.reduce(
                          (acc, li) => acc + (Number(li.amount) || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </Fragment>
              ))}
              <tr>
                <td
                  colSpan={6}
                  style={{
                    textAlign: 'right',
                    borderTop: '2px solid var(--brand-ink, #111827)',
                    paddingTop: 8,
                  }}
                >
                  <strong>Invoice Total</strong>
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    borderTop: '2px solid var(--brand-ink, #111827)',
                    paddingTop: 8,
                  }}
                >
                  <strong>{formatCurrency(mainTotal)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {/* Always-visible "Add Line Item" affordance — lets the
            Director tack on a last-minute charge (or a custom
            adjustment line) without having to bounce back to a
            timesheet. Adds an editable row with the same column
            shape as the auto-generated lines. */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 12,
          }}
        >
          {removedLineCount > 0 && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={restoreRemovedLines}
              disabled={generating}
              style={{ marginRight: 8 }}
            >
              Restore {removedLineCount} Removed{' '}
              {removedLineCount === 1 ? 'Line' : 'Lines'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => addManualLineItem()}
            disabled={generating}
          >
            + Add Adjustment
          </button>
        </div>
      </div>

      {/* ---------- Separate documents preview ---------- */}
      {(eligible.supplementals.length > 0 ||
        eligible.creditMemos.length > 0) && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">Separate Documents</div>
          <p className="muted" style={{ marginTop: 0 }}>
            These adjustments are flagged for their own paperwork rather
            than rolling into the main invoice — the Director picked
            "Issue Supplemental Invoice" or "Issue Credit Memo" when
            creating them.
          </p>
          {eligible.supplementals.length > 0 && (
            <table className="mini-table" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th colSpan={3}>Supplemental Invoices (own −A number)</th>
                </tr>
                <tr>
                  <th>Submitter / Week</th>
                  <th>Reason</th>
                  <th style={{ textAlign: 'right' }}>Hours / Amount</th>
                </tr>
              </thead>
              <tbody>
                {eligible.supplementals.map((s) => {
                  const rows = Array.isArray(s.rows) ? s.rows : [];
                  let hours = 0;
                  for (const r of rows) {
                    const v = r.values || {};
                    for (const k of Object.keys(v)) hours += Number(v[k]) || 0;
                  }
                  return (
                    <tr key={s.id}>
                      <td>
                        {s.SubmitterName} · {formatDateShort(s.AdjustmentDate || s.WeekStart)}
                      </td>
                      <td>{s.AdjustmentReason || s.SupplementReason || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {hours.toFixed(2)} hrs
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {eligible.creditMemos.length > 0 && (
            <table className="mini-table">
              <thead>
                <tr>
                  <th colSpan={3}>Credit Memos (CR-#### number)</th>
                </tr>
                <tr>
                  <th>Submitter / Week</th>
                  <th>Reason</th>
                  <th style={{ textAlign: 'right' }}>Credit Amount</th>
                </tr>
              </thead>
              <tbody>
                {eligible.creditMemos.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {c.SubmitterName} · {formatDateShort(c.AdjustmentDate || c.WeekStart)}
                    </td>
                    <td>{c.AdjustmentReason || c.SupplementReason || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(Number(c.CreditAmount) || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ---------- Generate footer ---------- */}
      {generationError && (
        <div
          style={{
            marginTop: 16,
            padding: '10px 14px',
            borderRadius: 6,
            background: 'rgba(220, 38, 38, 0.08)',
            color: 'var(--brand-danger)',
            fontSize: 13,
            whiteSpace: 'pre-line',
          }}
        >
          {generationError}
        </div>
      )}
      <div
        style={{
          marginTop: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
        }}
      >
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => navigate('/invoices')}
          disabled={generating}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onGenerate}
          disabled={generating}
        >
          {generating ? 'Generating…' : 'Generate Invoice'}
        </button>
      </div>
    </>
  );
}

// =====================================================================
// Helpers
// =====================================================================

// Shared style for the Billing Period & Mode row controls. Explicit
// `height` + `boxSizing` so `<input type="date">` and `<select>`
// render at the same visual height — without it, browsers give
// selects a few extra pixels of intrinsic height and the dropdown
// floats below the date inputs.
const dateInputStyle = {
  padding: '8px 10px',
  border: '1px solid var(--brand-border)',
  borderRadius: 6,
  fontSize: 14,
  height: 38,
  boxSizing: 'border-box',
  background: '#fff',
};

// Description options for the manual line-item dropdown. Same five
// values the auto-computed builder uses so the saved record reads
// consistently no matter which path the line came from.
// On-Call Engagement Fee (per Jillian, Oct 2026).
const ON_CALL_FEE_SECTION = 'onCallFee';
const ON_CALL_FEE_AMOUNT = 500;

const MANUAL_DESCRIPTION_OPTIONS = [
  'Resident Support Hours',
  'OT Resident Support Hours',
  'Mileage',
  'Expenses',
  'Credit',
];

// Inline editable row for a manual line item. Renders into the same
// 7-column table the auto lines use (Description, Details, Liaison,
// Dates, Quantity, Rate, Amount) but every cell is an editable
// input. Plant lives on the manual line itself (set via the plant
// dropdown). Used by the Line Items table when `li.isManual` is
// true. `onChange` receives a patch object; `onRemove` deletes the
// line.
// Applies a Director's edit to a line item. When Quantity or Rate
// changes, Amount recalculates as Quantity x Rate, unless the
// Director has typed an Amount by hand on this line (then their
// Amount wins until they change it again).
//   target: the object being updated (a manual line, or an override map)
//   current: the line's effective values (used for the math)
function applyLinePatch(target, current, patch) {
  const next = { ...target, ...patch };
  if ('amount' in patch) {
    next.amountTyped = true;
    return next;
  }
  if (('quantity' in patch || 'rate' in patch) && !target.amountTyped) {
    const q = 'quantity' in patch ? patch.quantity : current.quantity;
    const r = 'rate' in patch ? patch.rate : current.rate;
    if (r !== '' && r != null && q !== '' && q != null) {
      const amt = Math.round(Number(q) * Number(r) * 100) / 100;
      if (Number.isFinite(amt)) next.amount = amt;
    }
  }
  return next;
}

// One editable invoice row. Used for hand-added lines and (with
// `computed`) for the lines calculated from timesheets, so every
// value on the draft can be changed before the invoice generates.
function ManualLineRow({
  li,
  onChange,
  onRemove,
  onReset,
  plantOptions = [],
  computed = false,
  edited = false,
  extras = null,
}) {
  const inputStyle = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid var(--brand-border)',
    borderRadius: 4,
    fontSize: 13,
    background: '#fff',
    boxSizing: 'border-box',
  };
  const numberInputStyle = { ...inputStyle, textAlign: 'right' };
  const fromDate = String(li.dateRangeStart || li.weekStart || '').slice(0, 10);
  const toDate = String(
    li.dateRangeEnd || (li.weekStart ? addDaysIso(li.weekStart, 6) : ''),
  ).slice(0, 10);
  const smallBtn = {
    background: 'transparent',
    border: '1px solid var(--brand-border)',
    borderRadius: 4,
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: 12,
  };
  let rowStyle;
  if (li.orphan) {
    rowStyle = { background: 'rgba(220, 38, 38, 0.06)' };
  } else if (!computed || edited) {
    rowStyle = { background: 'rgba(232, 119, 34, 0.04)' };
  }
  return (
    <tr style={rowStyle}>
      <td>
        {computed ? (
          <input
            type="text"
            value={li.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Description"
            style={inputStyle}
          />
        ) : (
          <>
            {/* Plant picker: hidden on lines added with a section's
                "+ Add Line" link, since those already belong to that
                section's plant. Assigns the line to a plant so it groups
                under the right banner (and prints under that plant on
                the PDF). Left as "No plant" by default. */}
            {!li.lockedSection && (
            <select
              value={li.plantCode || ''}
              onChange={(e) => {
                const code = e.target.value;
                const match = plantOptions.find((o) => o.value === code);
                onChange({ plantCode: code, plantTitle: match?.title || '' });
              }}
              style={{ ...inputStyle, marginBottom: 6 }}
            >
              <option value="">— No plant —</option>
              {plantOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            )}
            <input
              type="text"
              list="manual-description-options"
              value={li.description || ''}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder="Pick or type a description"
              style={inputStyle}
            />
            <datalist id="manual-description-options">
              {MANUAL_DESCRIPTION_OPTIONS.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          </>
        )}
        {extras}
        {computed && edited && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            <span style={{ color: 'var(--brand-muted)' }}>Edited. </span>
            <button
              type="button"
              onClick={onReset}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--brand-primary, #E87722)',
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              Reset to Timesheet Values
            </button>
          </div>
        )}
      </td>
      <td>
        <input
          type="text"
          value={li.details || ''}
          onChange={(e) => onChange({ details: e.target.value })}
          placeholder="Notes (optional)"
          style={inputStyle}
        />
      </td>
      <td>
        <input
          type="text"
          value={li.submitterName || ''}
          onChange={(e) => onChange({ submitterName: e.target.value })}
          placeholder="—"
          style={inputStyle}
        />
      </td>
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            type="date"
            value={fromDate}
            onChange={(e) =>
              onChange({
                dateRangeStart: e.target.value,
                dateRangeEnd: toDate || e.target.value,
              })
            }
            title="From"
            style={inputStyle}
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) =>
              onChange({
                dateRangeStart: fromDate || e.target.value,
                dateRangeEnd: e.target.value,
              })
            }
            title="To"
            style={inputStyle}
          />
        </div>
      </td>
      <td style={{ textAlign: 'right' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            type="number"
            step="0.01"
            value={li.quantity ?? ''}
            onChange={(e) => onChange({ quantity: e.target.value })}
            placeholder="0"
            style={{ ...numberInputStyle, width: 80 }}
          />
          <input
            type="text"
            value={li.unitLabel || ''}
            onChange={(e) => onChange({ unitLabel: e.target.value })}
            placeholder="hrs"
            style={{ ...inputStyle, width: 50 }}
          />
        </div>
      </td>
      <td style={{ textAlign: 'right' }}>
        <input
          type="number"
          step="0.01"
          value={li.rate ?? ''}
          onChange={(e) => onChange({ rate: e.target.value })}
          placeholder="0.00"
          style={{ ...numberInputStyle, minWidth: 90 }}
        />
      </td>
      <td style={{ textAlign: 'right' }}>
        <div
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <input
            type="number"
            step="0.01"
            value={li.amount ?? 0}
            onChange={(e) =>
              onChange({ amount: Number(e.target.value) || 0 })
            }
            placeholder="0.00"
            style={{ ...numberInputStyle, width: 100 }}
          />
          <button
            type="button"
            onClick={onRemove}
            title="Remove this line"
            aria-label="Remove line item"
            style={{ ...smallBtn, color: 'var(--brand-danger)' }}
          >
            ✕
          </button>
        </div>
      </td>
    </tr>
  );
}

// Vertically-stacked "label on top, control below" wrapper. Used by
// the Billing Period & Mode row so the From / To / Billing Mode
// columns share an identical label height + spacing — that's what
// keeps the bottom edges of the inputs flush across the row.
function PeriodField({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label
        style={{
          // Match the standard .field label styling so the From / To /
          // Bill Schedule / Billing Mode row reads consistently with
          // Issue Date / Due Date / PO Number above it (dark ink,
          // 13px / 500). Was 12px / 600 / muted, which made these
          // labels look like secondary metadata.
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--brand-ink)',
          lineHeight: '16px',
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

// Build an empty manual line-item row. Shared by the "+ Add Line
// Item" button and the blank-invoice initial state so the shape stays
// identical no matter where the row originates.
function makeBlankManualLine(idx) {
  return {
    isManual: true,
    sourceKey: `manual-${idx}`,
    category: 'Manual',
    plantCode: '',
    plantTitle: '',
    weekStart: '',
    dateRangeStart: '',
    dateRangeEnd: '',
    coverageType: '',
    description: '',
    details: '',
    quantity: '',
    unitLabel: '',
    rate: null,
    amount: 0,
    submitterName: '',
  };
}

function labelSort(...keys) {
  return (a, b) => {
    for (const k of keys) {
      const av = String(a[k] || '');
      const bv = String(b[k] || '');
      if (av !== bv) return av.localeCompare(bv);
    }
    return 0;
  };
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function addDaysIso(iso, days) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

function formatPeriodLabel(start, end) {
  if (!start || !end) return '';
  return `${formatDateShort(start)} – ${formatDateShort(end)}`;
}

// Render an arbitrary [start, end] ISO range as a compact
// MM/DD-MM/DD label. Used by the Line Items "Dates" column so each
// line shows the actual range of days that contributed (clamped to
// the billing period), not the full Mon-Sun work week. Returns
// just MM/DD when start === end. Returns '' when start is missing
// so callers can fall back to a week-level format.
function formatDateRangeShort(startIso, endIso) {
  if (!startIso) return '';
  const fmt = (iso) => {
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    return `${m[2]}/${m[3]}`;
  };
  const s = fmt(startIso);
  if (!endIso || endIso === startIso) return s;
  const e = fmt(endIso);
  return e ? `${s}-${e}` : s;
}

// Render the week beginning on `weekStartIso` as a compact MM/DD-MM/DD
// label (e.g. "04/22-04/28"). Used by the Line Items "Dates" column.
// Parses ISO components manually so we don't shift the day in
// negative-offset timezones.
function formatWeekRangeShort(weekStartIso) {
  if (!weekStartIso) return '';
  const m = String(weekStartIso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return weekStartIso;
  const start = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const mmdd = (d) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  return `${mmdd(start)}-${mmdd(end)}`;
}

function composeAddress(supplier) {
  if (!supplier) return '';
  const parts = [
    supplier.AddressStreet,
    [supplier.City, supplier.State, supplier.PostalCode]
      .filter(Boolean)
      .join(', '),
    formatCountry(supplier.Country),
  ].filter(Boolean);
  return parts.join('\n');
}

function distinctPlants(timesheet) {
  const set = new Set();
  for (const r of timesheet.rows || []) {
    const code = String(r.plantCode || '').trim().toUpperCase();
    if (code) set.add(code);
  }
  return Array.from(set);
}

function buildSupplementLines(supplement, supplier) {
  const out = [];
  const sc = String(supplier?.SupplierCode || '').toUpperCase();
  // Flat-amount supplement (standalone adjustment, no rows) — emit a
  // single line for the whole SupplementAmount. Legacy row-bearing
  // supplements fall through to the per-row pricing below.
  const flat = Math.abs(Number(supplement.SupplementAmount) || 0);
  if (!(Array.isArray(supplement.rows) && supplement.rows.length > 0) && flat > 0) {
    const plantCode = String(supplement.AdjustmentPlantCode || '').toUpperCase();
    const reason =
      supplement.AdjustmentReason || supplement.SupplementReason || 'supplement';
    out.push({
      category: 'Supplement',
      plantCode: plantCode || '—',
      weekStart: supplement.WeekStart || '',
      description: `Supplement — ${reason}`,
      quantity: 1,
      unitLabel: '',
      rate: null,
      amount: flat,
    });
    return out;
  }
  for (const row of supplement.rows || []) {
    const rowSc = String(row.supplierCode || '').toUpperCase();
    if (rowSc !== sc) continue;
    const plantCode = String(row.plantCode || '').toUpperCase();
    if (!plantCode) continue;
    const coverageType = row.coverageType || 'Standard';
    if (coverageType === 'Expenses') {
      const amount = Number(row.expenseAmount) || 0;
      if (amount > 0) {
        out.push({
          category: 'Expense',
          plantCode,
          weekStart: supplement.WeekStart || '',
          description: `Expense — ${plantCode} — ${row.explanation || 'supplement'}`,
          quantity: 1,
          unitLabel: '',
          rate: null,
          amount,
        });
      }
      continue;
    }
    const values = row.values || {};
    let unitsTotal = 0;
    for (const k of Object.keys(values)) unitsTotal += Number(values[k]) || 0;
    if (unitsTotal <= 0) continue;
    if (coverageType === 'Mileage') {
      out.push({
        category: 'Mileage',
        plantCode,
        weekStart: supplement.WeekStart || '',
        description: 'Mileage reimbursement',
        quantity: unitsTotal,
        unitLabel: 'mi',
        rate: null,
        amount: calcMileageDollars(unitsTotal),
      });
    } else {
      // Labor on a supplemental — use the rate effective at week
      // start. Supplemental invoices are typically small one-off
      // catches so a single rate per supplement is fine.
      const rate = getRateFor(supplier, plantCode, supplement.WeekStart);
      out.push({
        category: 'Liaison Support',
        plantCode,
        weekStart: supplement.WeekStart || '',
        description: 'Liaison Support',
        quantity: unitsTotal,
        unitLabel: 'hrs',
        rate,
        amount: rate != null ? unitsTotal * rate : 0,
      });
    }
  }
  return out;
}

// The PO number for a given plant on this supplier — the per-plant
// `po.number` set on the Supplier form (mirrored into the assignment's
// legacy `currentPO` field by the supplier service, which doubles as
// the fallback for records saved before per-plant POs).
function poForPlant(supplier, plantCode, service) {
  // With several rows at one plant, the PO is read from the matching
  // service row (falling back to the plant's default row when the caller
  // only knows the plant). In practice a plant's rows share one PO.
  const pa = findAssignment(supplier, plantCode, service);
  return String(pa?.po?.number || pa?.currentPO || '').trim();
}

// Build the main invoice record(s). Combined mode produces a single
// record covering every plant; per-plant mode produces one record
// per plant with its own InvoiceNumber and its own subset of line
// items. Returns an array of generated-doc envelopes (record +
// pdfPayload) so the caller can write them all and download all PDFs.
async function buildMainInvoiceRecords({
  supplier,
  billingMode,
  lineItems,
  plantCodesOnInvoice,
  linesByPlant,
  appendix,
  linkedTimesheetIds,
  linkedAdjustmentIds,
  periodStart,
  periodEnd,
  issueDate,
  dueDate,
  poNumber,
  // True once the Director hand-edited the PO Number field — their
  // override then applies verbatim to every generated record instead
  // of the per-plant auto-fill.
  poTouched,
  notes,
  denormalizedManagers,
  createdBy,
  contactNames,
  billingContacts,
  billSchedule,
  paymentTerms,
}) {
  const docs = [];

  if (billingMode === BILLING_MODES.PER_PLANT && plantCodesOnInvoice.length > 1) {
    for (const plant of plantCodesOnInvoice) {
      const plantLines = lineItems.filter((li) => li.plantCode === plant);
      if (plantLines.length === 0) continue;
      const total = plantLines.reduce(
        (acc, li) => acc + (Number(li.amount) || 0),
        0,
      );
      // Per-plant split → ACCT-MMDDYY-PLANT (WE marker when weekly).
      const number = await nextInvoiceNumber(supplier.SupplierCode, {
        periodStart,
        periodEnd,
        plantCode: plant,
      });
      const record = await createInvoice({
        InvoiceNumber: number,
        Title: number,
        Status: 'Sent',
        SupplierCode: supplier.SupplierCode,
        SupplierName: supplier.Title,
        CustomerName: supplier.Title,
        BillingAddress: composeAddress(supplier),
        plantCodes: [plant],
        PeriodStart: periodStart,
        PeriodEnd: periodEnd,
        IssueDate: issueDate,
        DueDate: dueDate,
        // Each per-plant invoice stamps ITS plant's PO number — that's
        // the whole point of per-plant POs. A hand-edited PO Number
        // field wins for every record; otherwise fall back to the
        // draft field for plants without a PO of their own.
        PoNumber: poTouched ? poNumber : poForPlant(supplier, plant) || poNumber,
        Notes: notes,
        Total: total,
        RegionalManager: denormalizedManagers.rm,
        CustomerLiaisonManager: denormalizedManagers.lm,
        BillingMode: BILLING_MODES.PER_PLANT,
        lineItems: plantLines,
        // Each per-plant record claims the same underlying
        // timesheets because a single timesheet can have rows for
        // both plants. The "billed" detection on /invoices is
        // per-(supplier, timesheet), so this duplication is
        // correct — the timesheet stays out of future drafts no
        // matter which per-plant record references it.
        timesheetIds: linkedTimesheetIds,
        adjustmentIds: linkedAdjustmentIds,
        CreatedBy: createdBy,
        contactNames: contactNames || [],
        billingContacts,
        BillSchedule: billSchedule || '',
        // Supplier's Payment Terms (was missing here, so PDFs fell back to
        // "Net 30"; fixed Oct 2026).
        PaymentTerms: paymentTerms || '',
      });
      docs.push({
        kind: 'main-per-plant',
        record,
        pdfPayload: {
          invoice: record,
          periodLabel: formatPeriodLabel(periodStart, periodEnd),
          customer: supplier,
          lineItems: plantLines,
          // Per-plant split: the appendix only covers this plant.
          appendix:
            appendix && appendix.plants
              ? { plants: appendix.plants.filter((p) => p.plantCode === plant) }
              : null,
          total,
          isCreditMemo: false,
        },
      });
    }
    return docs;
  }

  // Combined mode (default). One invoice listing all plants.
  if (lineItems.length === 0) return docs;
  const total = lineItems.reduce(
    (acc, li) => acc + (Number(li.amount) || 0),
    0,
  );
  // Combined → ACCT-MMDDYY (ACCT-WEMMDDYY for a ≤7-day period).
  const number = await nextInvoiceNumber(supplier.SupplierCode, { periodStart, periodEnd });
  const record = await createInvoice({
    InvoiceNumber: number,
    Title: number,
    Status: 'Sent',
    SupplierCode: supplier.SupplierCode,
    SupplierName: supplier.Title,
    CustomerName: supplier.Title,
    BillingAddress: composeAddress(supplier),
    plantCodes: plantCodesOnInvoice,
    PeriodStart: periodStart,
    PeriodEnd: periodEnd,
    IssueDate: issueDate,
    DueDate: dueDate,
    PoNumber: poNumber,
    Notes: notes,
    Total: total,
    RegionalManager: denormalizedManagers.rm,
    CustomerLiaisonManager: denormalizedManagers.lm,
    BillingMode: BILLING_MODES.COMBINED,
    lineItems,
    timesheetIds: linkedTimesheetIds,
    adjustmentIds: linkedAdjustmentIds,
    CreatedBy: createdBy,
    BillSchedule: billSchedule || '',
    PaymentTerms: paymentTerms || '',
    // Combined invoices were missing the contacts (fixed Oct 2026).
    contactNames: contactNames || [],
    billingContacts,
  });
  docs.push({
    kind: 'main-combined',
    record,
    pdfPayload: {
      invoice: record,
      periodLabel: formatPeriodLabel(periodStart, periodEnd),
      customer: supplier,
      // The PDF groups these resolved lines itself (same order as the
      // on-screen preview), so every Director edit prints.
      lineItems,
      appendix,
      total,
      isCreditMemo: false,
    },
  });
  return docs;
}
